import { useState, useEffect, useCallback, useMemo, Fragment, type ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { PanelShell, PlnKpis, PlnKpi, PlnBloco } from './PanelShell'
import { FiLoader, FiRefreshCw, FiFlag, FiEdit2, FiCheck, FiX, FiPlus, FiTrash2, FiSearch, FiList, FiDownload, FiChevronRight, FiChevronDown, FiUploadCloud, FiAlertTriangle, FiImage, FiMapPin } from 'react-icons/fi'

// Painel DESAFIO — a PLANILHA do diretor (27/10/2026, "Desafio União.xlsx").
// A versão por consultoras ativas (DesafioPanel.tsx) ficou difícil de
// acompanhar; ele pediu "igual essa planilha". A ESTRUTURA é a dela — regras,
// Resumo do Mês e uma tabela por semana com as mesmas 11 colunas e as 3 faixas
// do % (≥ 100%, 80–99%, < 80%) — no desenho PLANOS do fin (cartões, blocos de
// papel, pln-tabela). Editar = as regras + o bloco "Grupos e médias".
// A conta mora no crm (lib/desafio-planilha.cjs, testada contra a planilha);
// o fin só repassa (routes/desafio-planilha.cjs). Só admin.
//
// ── Aba FOLLOW UP (27/10/2026, 13:58: "preciso subir aquele relatório que tem
// na segunda aba") ──────────────────────────────────────────────────────────
// A 2ª aba da planilha dele é o relatório "Follow Up": ele BAIXA DO SITE DE
// PEDIDOS, cola na aba `Sheet1` e a aba `Resultado` lê de lá com XLOOKUP. Por
// isso a planilha dele mostra a SEMANA CORRENTE, que o Draco ainda não tem — o
// lote só entra na terça à noite.
//
// Aqui a aba faz as duas pontas:
//   · MOSTRA o Follow Up da vitrine — Draco nas semanas que já fecharam
//     (conferido célula a célula contra a aba: 118 grupos, 10 distritos, 0
//     divergência) e o arquivo enviado na semana que ainda não fechou;
//   · RECEBE o arquivo do site (arrastar e soltar). A tela só vira o arquivo em
//     matriz com o SheetJS; quem lê é o crm (lib/followup-arquivo.cjs, testada
//     contra a aba real), que confere o arquivo contra o total do próprio
//     relatório e contra o Draco antes de deixar entrar.
// É a distribuição CAMPO GRANDE INTEIRA, não só os 16 grupos do desafio.

// ⚠ `incremento: null` só acontece no geral: ele varia de distrito para
// distrito e ali não existe UM incremento (a faixa vai em `variam`).
interface Regras { incremento: number | null; pesos: number[]; pedido: number; piso: number; grupos: string[] }
// 31/10/2026: regras por vitrine (`vitrines`), o desafio do mês digitado pela
// diretoria por grupo (`desafios[periodo][grupo]`) e o do distrito (`totais`).
interface Config extends Regras {
  medias: Record<string, Record<string, number>>
  desafios?: Record<string, Record<string, number>>
  // 04/11/2026: R$ que SOMAM ao desafio do grupo, por cima da regra ou do valor
  // da diretoria (`ajustes[periodo][grupo]`). Ver o doc da lib no crm.
  ajustes?: Record<string, Record<string, number>>
  totais?: Record<string, number>
  vitrines?: Record<string, Regras>
}
type Origem = 'regra' | 'diretoria' | 'total'
interface Linha {
  codigo: string; nome: string | null; media: number; peso_grupo: number; origem?: Origem
  // O acréscimo em R$ deste grupo (null = nenhum). `origem` continua dizendo de
  // onde saiu a BASE; o ajuste vem por cima dela.
  ajuste?: number | null
  desafio_mes: number; desafio_semana: number; pedidos_meta: number
  realizado: number | null; pedidos_realizados: number | null; dif_pedidos: number | null; atingimento: number | null
  // Persistência do resultado (28/10/2026): quanto o grupo já ganhou ou deve no
  // mês e quantos pedidos faltam para fechá-lo. Vêm do crm desde então; a
  // interface é que não tinha sido atualizada (o build não roda o tsc).
  pedidos_mes?: number; saldo_mes?: number | null; falta_fechar?: number | null
  // Só no geral: uma linha é um DISTRITO, e isto é quantos grupos ele tem no desafio.
  grupos?: number
}
interface Semana { n: number; semana: number; peso: number; estado: 'fechada' | 'andamento' | 'futura'; linhas: Linha[]; total: Omit<Linha, 'codigo' | 'nome'>; fonte?: 'draco' | 'arquivo' | null; distritos_sem_dado?: string[] }
interface ResumoSem { n: number; semana: number; peso: number; desafio: number; realizado: number; atingimento: number | null; dif_pedidos: number | null; fonte?: 'draco' | 'arquivo' | null }
interface Grupo { codigo: string; nome: string | null; media: number; media_digitada: boolean; media_draco: number; desafio_mes?: number; desafio_regra?: number; base_mes?: number; ajuste?: number | null; origem?: Origem }
interface Planilha {
  semana_atual: number; periodo: number; vitrine: number; config: Config
  grupos: Grupo[]; semanas: Semana[]
  resumo: { semanas: ResumoSem[]; total: { peso: number; desafio: number; realizado: number; atingimento: number | null; dif_pedidos: number }; acumulado: number | null }
  atualizado_por?: string | null; atualizado_em?: string | null
  envios?: Envio[]
  // De que vitrine vieram as regras (null = a base, a config de antes).
  regras_de?: number | null
  desafio_total?: number | null; desafio_regra_total?: number; desafio_soma_diretoria?: number
  // ⚠ `proposta` = ainda NÃO há desafio salvo para este distrito; o que está na
  // tela é sugestão (regras da União + os grupos que venderam, média do Draco).
  distrito?: string; proposta?: boolean; distritos?: Distrito[]
  // ── TODA A DISTRIBUIÇÃO (03/11/2026) ──────────────────────────────────────
  // `geral` = esta resposta é a SOMA dos distritos, uma linha por distrito e
  // só leitura. `fora` = distritos que ficaram de fora por ainda não terem
  // desafio salvo (proposta não entra em soma). `variam` = a faixa do que é
  // diferente entre eles; o campo correspondente em `config` vem null.
  geral?: boolean; fora?: Distrito[]
  variam?: { incremento: [number, number] | null; pedido: [number, number] | null; piso: [number, number] | null; pesos: boolean }
}
interface GrupoDraco { codigo: string; nome: string; distrito: string | null }
// Um distrito no seletor: quantos grupos venderam na vitrine e se ele já tem
// desafio salvo. O diretor está levantando os dados dos outros distritos
// (27/10/2026), então essa marca é o andamento do trabalho dele.
interface Distrito { codigo: string; nome: string; grupos: number; tem_desafio: boolean }

// Follow Up: uma célula é { venda, novas } e some (null) na semana sem lote.
interface FuCel { qs: number | null; f: number | null }
interface FuSoma { semanas: FuCel[]; total: FuCel }
interface FuGrupo extends FuSoma { codigo: string; nome: string | null }
interface FuDistrito extends FuSoma { codigo: string; nome: string | null; grupos: FuGrupo[] }
interface FuSemana { n: number; semana: number; tem_lote: boolean; estado: Semana['estado']; fonte?: 'draco' | 'arquivo' | null }
// O "falar com a API" do painel, passado aos componentes filhos.
type ApiFn = (u: string, i?: RequestInit) => Promise<Record<string, unknown>>

interface Envio { semana: number; arquivo: string | null; titulo: string | null; grupos: number; venda: number; novas: number; por: string | null; em: string | null }
interface FollowUp {
  semana_atual: number; periodo: number; grupos: number
  semanas: FuSemana[]; distritos: FuDistrito[]; total: FuSoma; vendendo: (number | null)[]
  envios?: Envio[]
}
// O que o crm entendeu do arquivo, antes de gravar.
interface SemanaArquivo { semana: number; venda: number; novas: number; fechada: boolean; entra: boolean; draco: { venda: number; novas: number } | null; confere: boolean | null }
interface Conferido {
  cabecalho: { titulo: string | null; distribuicao: string | null; representante: string | null; previa: boolean }
  semanas: SemanaArquivo[]; grupos: number; distritos: { codigo: string; nome: string; grupos: number }[]
}

// ⚠ Abaixo de 640px a `.pln-tabela` do PLANOS vira CARTÃO por linha: o thead
// some e cada célula lê "rótulo · valor", com o rótulo vindo de `data-rot` no
// JSX. SEM data-rot o celular mostra só a coluna de números, sem nome nenhum —
// medido em 390px em 27/10, e o diretor lê no celular. Toda `<td>` que não é a
// primeira (essa vira o título do cartão) leva o rótulo da coluna dela.
// ── Formatos da planilha: R$ sem centavos, "-" no zero, negativo em vermelho ──
const inteiro = (n: number) => Math.round(Math.abs(n)).toLocaleString('pt-BR')
const ruim = (s: string) => <span className="pln-ruim">{s}</span>
function brl(n: number | null | undefined): ReactNode {
  if (n == null || Math.round(n) === 0) return <span className="text-[var(--pln-tinta-3)]">–</span>
  return n < 0 ? ruim(`−R$ ${inteiro(n)}`) : `R$ ${inteiro(n)}`
}
function qtd(n: number | null | undefined): ReactNode {
  if (n == null || Math.round(n) === 0) return <span className="text-[var(--pln-tinta-3)]">–</span>
  return n < 0 ? ruim(`−${inteiro(n)}`) : inteiro(n)
}
function dif(n: number | null | undefined): ReactNode {
  if (n == null) return <span className="text-[var(--pln-tinta-3)]">–</span>
  if (Math.round(n) === 0) return '0'
  return n < 0 ? ruim(`−${inteiro(n)}`) : <span className="text-[var(--pln-verde)]">+{inteiro(n)}</span>
}
// O número exato, com uma casa, para o balão de ajuda. A coluna mostra inteiro
// (como o resto da tabela), e aí +3 numa semana com −4 na outra fecha em "0" —
// que está CERTO (o cru é −0,3: elas se anularam), mas quem lê soma de cabeça e
// acha −1. O valor preciso fica a um toque, em vez de virar uma dúvida sem
// resposta na frente do diretor.
const comCasa = (n: number | null | undefined) =>
  n == null ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

// Quanto ainda falta para fechar o mês. Zero ou abaixo = objetivo já batido, e
// isso merece ser DITO: um "0" seco no meio de uma coluna de números grandes
// se lê como "não tem dado", que é o contrário do que aconteceu.
function falta(n: number | null | undefined): ReactNode {
  if (n == null) return <span className="text-[var(--pln-tinta-3)]">–</span>
  if (Math.round(n) <= 0) return <span className="text-[var(--pln-verde)]">fechado</span>
  return inteiro(n)
}
const pct = (n: number | null | undefined, casas = 1) => (n == null ? '–' : `${(n * 100).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`)
const sem = (s: number) => `${s % 100}/${Math.floor(s / 100)}`

// ── Seletor de período (31/10/2026: "colocar aqui as opções pra selecionar o
// período") ────────────────────────────────────────────────────────────────
// O mesmo calendário aritmético do crm (lib/desafio-planilha.cjs: 52 semanas,
// 13 vitrines de 4). O crm aceita `?semana=AAAASS` e monta a vitrine dessa
// semana; o que muda é QUAL semana mandar:
//   · vitrine passada → a ÚLTIMA semana dela (com a 1ª, as outras três viriam
//     como "ainda não começou" e o realizado sumiria);
//   · vitrine atual   → nada (o crm usa a semana corrente);
//   · próxima vitrine → a 1ª semana dela: tudo futuro, só o objetivo.
const SEM_ANO = 52
const idxSem = (s: number) => Math.floor(s / 100) * SEM_ANO + (s % 100) - 1
const semDoIdx = (i: number) => Math.floor(i / SEM_ANO) * 100 + (i % SEM_ANO) + 1
const periodoDoIdx = (i: number) => { const v = Math.floor(i / 4); return Math.floor(v / 13) * 100 + (v % 13) + 1 }
interface OpcaoPeriodo { chave: string; periodo: number; semana: number | null; tipo: 'passada' | 'atual' | 'proxima'; ultima: number }
function opcoesPeriodo(hoje: number): OpcaoPeriodo[] {
  const ini = idxSem(hoje) - (idxSem(hoje) % 4)
  return [-2, -1, 0, 1].map(k => {
    const base = ini + k * 4
    const tipo = k < 0 ? 'passada' : k === 0 ? 'atual' : 'proxima'
    return {
      chave: String(periodoDoIdx(base)), periodo: periodoDoIdx(base), tipo, ultima: semDoIdx(base + 3),
      semana: tipo === 'passada' ? semDoIdx(base + 3) : tipo === 'proxima' ? semDoIdx(base) : null,
    }
  })
}
const rotuloPeriodo = (o: OpcaoPeriodo) =>
  `vitrine ${o.periodo % 100}/${Math.floor(o.periodo / 100)}${o.tipo === 'atual' ? ' · atual' : o.tipo === 'proxima' ? ' · próxima' : ''}`

// As 3 faixas da formatação condicional da planilha, como selo.
function Selo({ v }: { v: number | null | undefined }) {
  if (v == null) return <span className="text-[var(--pln-tinta-3)]">–</span>
  const cor = v >= 1
    ? 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:ring-emerald-900'
    : v >= 0.8
      ? 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:ring-amber-900'
      : 'bg-rose-50 text-rose-800 ring-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:ring-rose-900'
  return <span className={`inline-block min-w-[62px] text-center rounded-md px-2 py-0.5 text-[12.5px] font-semibold ring-1 ring-inset ${cor}`}>{pct(v)}</span>
}

const ESTADO: Record<Semana['estado'], string> = { fechada: 'Fechada', andamento: 'Em andamento', futura: 'Ainda não começou' }
// De onde veio o realizado da semana — o diretor precisa saber se o número já é
// o apurado do Draco ou ainda é o relatório que ele mesmo enviou.
const FONTE: Record<string, string> = {
  draco: ' · realizado apurado no Draco',
  arquivo: ' · realizado do relatório do site que você enviou',
}
const SEMANA_NOME = ['1ª semana', '2ª semana', '3ª semana', '4ª semana']
const n = 'text-right tabular-nums whitespace-nowrap'

function CampoNum({ valor, onChange, sufixo, largura = 'w-24' }: { valor: number; onChange: (v: number) => void; sufixo?: string; largura?: string }) {
  const [txt, setTxt] = useState(String(valor).replace('.', ','))
  useEffect(() => { setTxt(String(valor).replace('.', ',')) }, [valor])
  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        inputMode="decimal" value={txt}
        onChange={e => { setTxt(e.target.value); const v = Number(e.target.value.replace(/\./g, '').replace(',', '.')); if (Number.isFinite(v)) onChange(v) }}
        className={`${largura} text-right tabular-nums rounded-lg border border-[var(--pln-borda)] bg-[var(--pln-papel)] px-2.5 py-1.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--pln-verde-vivo)]`} />
      {sufixo && <span className="text-[var(--pln-tinta-3)]">{sufixo}</span>}
    </span>
  )
}
// Campo que pode ficar VAZIO — vazio = "pela regra". Usado no desafio do mês
// definido pela diretoria (por grupo e do distrito). `dica` = o valor da regra,
// como placeholder: ele vê o que valeria sem digitar nada.
function CampoOpcional({ valor, onChange, dica, largura = 'w-32' }: { valor: number | null | undefined; onChange: (v: number | null) => void; dica?: string; largura?: string }) {
  const fmt = (v: number | null | undefined) => (v == null ? '' : String(Math.round(v * 100) / 100).replace('.', ','))
  const [txt, setTxt] = useState(fmt(valor))
  useEffect(() => { setTxt(fmt(valor)) }, [valor])
  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        inputMode="decimal" value={txt} placeholder={dica}
        onChange={e => {
          setTxt(e.target.value)
          const t = e.target.value.trim()
          if (!t) { onChange(null); return }
          const v = Number(t.replace(/\./g, '').replace(',', '.'))
          if (Number.isFinite(v) && v >= 0) onChange(v)
        }}
        className={`${largura} text-right tabular-nums rounded-lg border border-[var(--pln-borda)] bg-[var(--pln-papel)] px-2.5 py-1.5 text-[14px] placeholder:text-[var(--pln-tinta-3)] focus:outline-none focus:ring-2 focus:ring-[var(--pln-verde-vivo)]`} />
      {valor != null && <button type="button" onClick={() => onChange(null)} title="voltar para a regra" className="text-[var(--pln-tinta-3)] hover:text-[var(--pln-ruim)] p-0.5"><FiX /></button>}
    </span>
  )
}

// A etiqueta de quem definiu o desafio do mês do grupo.
function EtiquetaOrigem({ o }: { o?: Origem }) {
  if (!o || o === 'regra') return null
  return <span className="ml-1.5 inline-block rounded px-1.5 py-px text-[10.5px] font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-300 dark:ring-indigo-900 align-middle">{o === 'diretoria' ? 'diretoria' : 'total diretoria'}</span>
}

// O acréscimo em R$ do grupo, ao lado do nome (04/11/2026). Fica VISÍVEL na
// tabela porque é um número que a diretoria digitou e que muda o objetivo: sem
// a etiqueta, o grupo apareceria acima dos vizinhos sem nada explicando.
function EtiquetaAjuste({ v }: { v?: number | null }) {
  if (!v) return null
  const mais = v > 0 ? '+' : '−'
  return <span title="acréscimo em R$ sobre o desafio deste grupo" className="ml-1.5 inline-block rounded px-1.5 py-px text-[10.5px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:ring-amber-900 align-middle tabular-nums">{mais} R$ {inteiro(Math.abs(v))}</span>
}

const BTN = 'neu-btn px-3 py-1.5 rounded-lg border border-border text-sm flex items-center gap-1.5 disabled:opacity-50'
const BTN_PRI = 'px-3.5 py-1.5 rounded-lg bg-[var(--pln-verde)] text-white dark:text-[#0c1a19] text-sm font-semibold flex items-center gap-1.5 hover:opacity-90 disabled:opacity-40'

export function DesafioPlanilhaPanel() {
  const { authenticatedFetch } = useAuth()
  const [dados, setDados] = useState<Planilha | null>(null)
  const [rascunho, setRascunho] = useState<Config | null>(null)
  const [editando, setEditando] = useState(false)
  const [carregando, setCarregando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [escolher, setEscolher] = useState(false)
  const [aba, setAba] = useState<'desafio' | 'followup'>('desafio')
  const [baixando, setBaixando] = useState(false)
  const [baixandoImg, setBaixandoImg] = useState<number | null>(null)
  // null = "o que o servidor escolher" (a União, o único com desafio hoje).
  // 'geral' = TODA A DISTRIBUIÇÃO: a soma dos distritos, uma linha por distrito.
  const [distrito, setDistrito] = useState<string | null>(null)
  // null = a vitrine atual. `hoje` = a semana corrente que o crm devolveu na
  // vitrine atual — é dela que saem as opções do seletor.
  const [semanaSel, setSemanaSel] = useState<number | null>(null)
  const [hoje, setHoje] = useState<number | null>(null)
  // A última semana da vitrine atual já fechou no Draco? Sem ela, a média da
  // próxima vitrine sai menor que a real (ver o aviso na tela).
  const [ultimaFechada, setUltimaFechada] = useState(true)
  // O distrito da última leitura do servidor: o Salvar precisa dele, e `dados`
  // durante a edição é a resposta da /previa, que não traz o distrito.
  const [distritoAberto, setDistritoAberto] = useState<string | null>(null)
  // Sobe a cada Salvar / arquivo enviado: o gráfico de Evolução lê de novo.
  const [versao, setVersao] = useState(0)
  // A hora da última leitura. Fica na tela: número de painel sem idade é número
  // em que ninguém sabe se pode confiar (ver o `lido` no cabeçalho).
  const [lidoEm, setLidoEm] = useState<number | null>(null)
  const qs = useMemo(() => {
    const p = new URLSearchParams()
    if (distrito) p.set('distrito', distrito)
    if (semanaSel) p.set('semana', String(semanaSel))
    const s = p.toString()
    return s ? `?${s}` : ''
  }, [distrito, semanaSel])

  const api = useCallback(async (url: string, init?: RequestInit) => {
    const r = await authenticatedFetch(url, init)
    const d = await r.json().catch(() => ({}))
    if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`)
    return d
  }, [authenticatedFetch])

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const d: Planilha = await api(`/api/desafio-planilha${qs}`)
      setDados(d); setRascunho(null); setEditando(false); setLidoEm(Date.now())
      if (d.distrito) setDistritoAberto(d.distrito)
      if (!semanaSel) { setHoje(d.semana_atual); setUltimaFechada(d.semanas[3]?.fonte === 'draco') }
    } catch (e) { setErro((e as Error).message) } finally { setCarregando(false) }
  }, [api, qs, semanaSel])
  useEffect(() => { carregar() }, [carregar])

  // ⚠ As duas abas carregam cada uma a SUA conta, e só na abertura do painel.
  // Quando o arquivo do site entra na aba Follow Up, o realizado desta aba
  // MUDOU — mas ela não ficaria sabendo. Em 27/10 15:13 o diretor enviou o
  // arquivo, viu o carimbo "enviado por Ricardo Novais" na aba Follow Up e
  // voltou para o Desafio com os números velhos na tela: "subiu e não aplicou
  // em nada". Tinha aplicado; a tela é que não tinha ido buscar de novo.
  // Não uso o `carregar` aqui porque ele descarta o rascunho: se ele estiver no
  // meio de uma edição de regra, enviar o arquivo não pode apagar o que digitou.
  const recarregarRealizado = useCallback(async () => {
    try { const d: Planilha = await api(`/api/desafio-planilha${qs}`); setDados(d); setVersao(v => v + 1); setLidoEm(Date.now()) }
    catch (e) { setErro((e as Error).message) }
  }, [api, qs])

  // ── VOLTOU PARA A ABA: relê sozinha (03/11/2026) ──────────────────────────
  // A Juliane editou os 10 distritos e o GERAL, que estava aberto em outra aba,
  // continuou com os números velhos — "falta sincronismo". E estava mesmo: a
  // tela só ia buscar de novo ao trocar de distrito/vitrine ou no Atualizar.
  // É a MESMA família do 27/10, quando o arquivo entrava na aba Follow Up e o
  // Desafio ficava com o realizado velho.
  // ⚠ Não relê no meio de uma edição: isso apagaria o que ela está digitando.
  // ⚠ Nem se acabou de ler (15s), senão todo alt-tab vira uma ida ao Draco.
  useEffect(() => {
    const voltou = () => {
      if (document.visibilityState !== 'visible') return
      if (editando || rascunho) return
      if (lidoEm && Date.now() - lidoEm < 15000) return
      recarregarRealizado()
    }
    document.addEventListener('visibilitychange', voltou)
    window.addEventListener('focus', voltou)
    return () => {
      document.removeEventListener('visibilitychange', voltou)
      window.removeEventListener('focus', voltou)
    }
  }, [editando, rascunho, lidoEm, recarregarRealizado])

  // Editando: a conta recalcula no crm com o rascunho, meio segundo depois de parar de digitar.
  useEffect(() => {
    if (!rascunho) return
    let vivo = true
    const t = setTimeout(async () => {
      try {
        // A prévia é da MESMA vitrine que está na tela (sem ?semana o crm
        // voltaria para a atual no meio da edição da próxima).
        const d: Planilha = await api(`/api/desafio-planilha/previa${semanaSel ? `?semana=${semanaSel}` : ''}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ config: rascunho }) })
        if (vivo) setDados(atual => ({ ...d, atualizado_por: atual?.atualizado_por, atualizado_em: atual?.atualizado_em, regras_de: atual?.regras_de }))
      } catch (e) { if (vivo) setErro((e as Error).message) }
    }, 500)
    return () => { vivo = false; clearTimeout(t) }
  }, [rascunho, api, semanaSel])

  // ═════════════════════════════════════════════════════════════════════════
  // EXCEL DO DESAFIO — a aba `Resultado` dele de volta em planilha.
  // Sai na ORDEM DA TELA: o resumo do mês, depois as quatro semanas com os
  // grupos, e uma aba com as regras que produziram os números.
  // ⚠ Vai NÚMERO, não texto formatado: dinheiro com `#,##0.00` e percentual
  // como fração com `0.0%`. Se eu mandasse "R$ 110.325" ele não conseguiria
  // somar nada no Excel — e a planilha dele é feita de contas.
  // ⚠ A coluna "De onde vem" é o que evita a pergunta de quinta-feira: a
  // semana que ainda não fechou veio do arquivo que ele mesmo enviou, e isso
  // tem de viajar junto com o arquivo.
  // ═════════════════════════════════════════════════════════════════════════
  const baixarExcel = async () => {
    if (!dados) return
    setBaixando(true)
    try {
      const XLSX = await import('xlsx')
      const r = dados.resumo
      const ondeVem = (s: Semana) =>
        s.fonte === 'draco' ? 'apurado no Draco'
          : s.fonte === 'arquivo' ? 'relatório do site enviado no painel'
            : s.estado === 'andamento' ? 'ainda sem relatório' : 'semana não começou'
      const V = (x: number | null | undefined) => (x == null ? '' : x)
      // ⚠ A tela escreve "–" tanto para "sem número" quanto para zero. No
      // Excel os dois não podem virar a mesma coisa: célula VAZIA é "a semana
      // ainda não tem número", ZERO é "vendeu zero". Quem decide é a FONTE da
      // semana, não o valor — senão a 4ª semana sairia com R$ 0,00 de
      // realizado, como se tivesse fechado sem vender nada.
      const semNumero = (s: { fonte?: 'draco' | 'arquivo' | null }) => !s.fonte

      type Cel = string | number
      const aoa: Cel[][] = []
      const dinheiro: string[] = []   // células de R$
      const porcento: string[] = []   // células de %
      const inteiro: string[] = []    // células de contagem
      const negrito: number[] = []    // linhas de título/soma
      const cel = (lin: number, col: number) => XLSX.utils.encode_cell({ r: lin, c: col })

      aoa.push([`Desafio — vitrine ${dados.vitrine}/${Math.floor(dados.periodo / 100)}`])
      negrito.push(0)
      aoa.push([`Semana corrente ${sem(dados.semana_atual)} · gerado em ${new Date().toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}${dados.atualizado_por ? ` · regras ajustadas por ${dados.atualizado_por}` : ''}`])
      aoa.push([])

      // ── Resumo do mês ───────────────────────────────────────────────────
      negrito.push(aoa.length); aoa.push(['Resumo do mês'])
      negrito.push(aoa.length)
      aoa.push(['Semana', '% do mês', 'Desafio', 'Realizado', 'Atingimento', 'Pedidos da semana', 'De onde vem o realizado'])
      for (const s of r.semanas) {
        const orig = dados.semanas.find(x => x.semana === s.semana)
        const lin = aoa.length
        // ⚠ a fonte que vale é a da SEMANA (`dados.semanas`), não a cópia dela
        // no resumo: o resumo é um espelho, e espelho pode vir sem o campo.
        const vazia = semNumero(orig || s)
        aoa.push([`${s.n}ª semana ${sem(s.semana)}`, s.peso, s.desafio,
          vazia ? '' : V(s.realizado), vazia ? '' : V(s.atingimento), vazia ? '' : V(s.dif_pedidos),
          orig ? ondeVem(orig) : ''])
        porcento.push(cel(lin, 1), cel(lin, 4)); dinheiro.push(cel(lin, 2), cel(lin, 3)); inteiro.push(cel(lin, 5))
      }
      const linT = aoa.length; negrito.push(linT)
      aoa.push(['Total do mês', r.total.peso, r.total.desafio, r.total.realizado, V(r.total.atingimento), r.total.dif_pedidos, ''])
      porcento.push(cel(linT, 1), cel(linT, 4)); dinheiro.push(cel(linT, 2), cel(linT, 3)); inteiro.push(cel(linT, 5))
      const linA = aoa.length
      aoa.push(['Atingimento acumulado', '', '', '', V(r.acumulado), '', 'nas semanas que já têm realizado'])
      negrito.push(linA); porcento.push(cel(linA, 4))
      aoa.push([])

      // ── Uma semana por bloco, com os grupos ─────────────────────────────
      // ⚠ 03/11/2026: no GERAL a coluna "Média vendas" SAI. A soma das médias
      // dos grupos ficava do lado do desafio e era lida como se fosse ele — a
      // Juliane leu o R$ 308.424 do VENCER como o desafio, que é R$ 299.171.
      // Na tela de um distrito ela FICA: ali é a coluna C da planilha do
      // diretor, a base de que o desafio sai (média × (1 + incremento)).
      const semMedia = geral
      const tira = (linha: Cel[]) => (semMedia ? linha.filter((_, i) => i !== 2) : linha)
      const ci = (c: number) => (semMedia && c > 2 ? c - 1 : c)
      const COLS = [geral ? 'Distrito' : 'Grupo', 'Código', 'Média vendas', 'Peso', 'Desafio mês', 'Desafio semana', 'Pedidos meta', 'Realizado', 'Pedidos feitos', 'Pedidos da semana', 'Atingimento', 'Saldo de Pedidos']
      for (const s of dados.semanas) {
        negrito.push(aoa.length)
        aoa.push([`${s.n}ª semana — ${sem(s.semana)} · ${pct(s.peso, 0)} do desafio do mês · ${ESTADO[s.estado]} · ${ondeVem(s)}`])
        negrito.push(aoa.length); aoa.push(tira([...COLS]))
        const vazia = semNumero(s)
        const linhas = [...s.linhas.map(l => ({ nome: l.nome || l.codigo, cod: l.codigo, d: l })), { nome: 'Total', cod: '', d: s.total }]
        for (const { nome, cod, d } of linhas) {
          const lin = aoa.length
          if (nome === 'Total') negrito.push(lin)
          aoa.push(tira([nome, cod, V(d.media), V(d.peso_grupo), V(d.desafio_mes), V(d.desafio_semana), V(d.pedidos_meta),
            vazia ? '' : V(d.realizado), vazia ? '' : V(d.pedidos_realizados), vazia ? '' : V(d.dif_pedidos), vazia ? '' : V(d.atingimento),
            vazia ? '' : V(d.falta_fechar)]))
          if (!semMedia) dinheiro.push(cel(lin, 2))
          dinheiro.push(cel(lin, ci(4)), cel(lin, ci(5)), cel(lin, ci(7)))
          porcento.push(cel(lin, ci(3)), cel(lin, ci(10)))
          inteiro.push(cel(lin, ci(6)), cel(lin, ci(8)), cel(lin, ci(9)), cel(lin, ci(11)))
        }
        aoa.push([])
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa)
      const largs = [{ wch: 30 }, { wch: 9 }, { wch: 14 }, { wch: 8 }, { wch: 14 }, { wch: 15 }, { wch: 12 }, { wch: 14 }, { wch: 13 }, { wch: 12 }, { wch: 13 }, { wch: 16 }, { wch: 36 }]
      ws['!cols'] = semMedia ? largs.filter((_, i) => i !== 2) : largs
      for (const k of dinheiro) if (ws[k] && typeof ws[k].v === 'number') ws[k].z = '#,##0.00'
      for (const k of porcento) if (ws[k] && typeof ws[k].v === 'number') ws[k].z = '0.0%'
      for (const k of inteiro) if (ws[k] && typeof ws[k].v === 'number') ws[k].z = '0'
      for (const lin of negrito) {
        for (let c = 0; c < (semMedia ? 11 : 12); c++) { const k = ws[cel(lin, c)]; if (k) k.s = { font: { bold: true } } }
      }

      // ── Aba das regras: de onde o desafio saiu ──────────────────────────
      const cf = dados.config
      const reg: Cel[][] = [
        ['Regras do desafio'],
        [],
        // ⚠ No geral não existe UM incremento (varia por distrito): vai a faixa,
        // em texto, e a célula não é formatada como porcentagem.
        ['Incremento sobre a média', cf.incremento == null
          ? `varia por distrito${dados.variam?.incremento ? ` · ${pct(dados.variam.incremento[0], 0)} a ${pct(dados.variam.incremento[1], 0)}` : ''}`
          : cf.incremento],
        ...cf.pesos.map((pz, k) => [`Peso da ${k + 1}ª semana`, pz] as Cel[]),
        ['Valor do pedido', cf.pedido],
        ['Média mínima do grupo', cf.piso],
        [],
        ['A conta', 'desafio do grupo = média das duas últimas vitrines × (1 + incremento), repartida pelo peso de cada semana'],
        ['Pedidos', 'pedidos meta = desafio da semana ÷ valor do pedido, arredondado para cima'],
        ['Realizado', 'venda da semana: apurada no Draco quando a semana fecha; antes disso, o relatório do site enviado no painel'],
      ]
      // ⚠ O acréscimo por grupo vai na aba das REGRAS, não como coluna nova na
      // aba Desafio: ali as colunas são posicionais (ver `tira()`/`ci()`) e uma
      // coluna a mais deslocaria todos os formatos. Aqui ele aparece inteiro,
      // com o total, e quem abrir o arquivo entende de onde veio a diferença.
      const comAjuste = (dados.grupos || []).filter(g => g.ajuste)
      const linhasAjuste: number[] = []
      if (comAjuste.length) {
        reg.push([])
        reg.push(['Acréscimo por grupo', 'somado ao desafio do grupo, por cima da regra ou do valor da diretoria'])
        for (const g of comAjuste) {
          linhasAjuste.push(reg.length)
          reg.push([`${g.nome || g.codigo} (${g.codigo})`, g.ajuste as number])
        }
        linhasAjuste.push(reg.length)
        reg.push(['Total do acréscimo', comAjuste.reduce((t, g) => t + (g.ajuste || 0), 0)])
      }
      const ws2 = XLSX.utils.aoa_to_sheet(reg)
      ws2['!cols'] = [{ wch: 26 }, { wch: 100 }]
      for (let k = cf.incremento == null ? 1 : 0; k < cf.pesos.length + 1; k++) { const c = ws2[XLSX.utils.encode_cell({ r: 2 + k, c: 1 })]; if (c) c.z = '0.0%' }
      for (const a of ['B8', 'B9']) { const c = ws2[a]; if (c) c.z = '#,##0.00' }
      for (const lin of linhasAjuste) { const c = ws2[XLSX.utils.encode_cell({ r: lin, c: 1 })]; if (c) c.z = '#,##0.00' }
      if (ws2.A1) ws2.A1.s = { font: { bold: true } }

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Desafio')
      XLSX.utils.book_append_sheet(wb, ws2, 'Regras')
      XLSX.writeFile(wb, `Desafio-vitrine-${dados.vitrine}-${Math.floor(dados.periodo / 100)}.xlsx`)
    } catch (e) { setErro((e as Error).message) } finally { setBaixando(false) }
  }

  // ═════════════════════════════════════════════════════════════════════════
  // A IMAGEM PARA O GRUPO DO WHATSAPP
  // Ele JÁ manda essa tabela no grupo toda semana — um print do Excel
  // ("WhatsApp Image 2026-10-27 at 15.25.25.jpeg"): Código, Grupo, Pedidos da semana
  // e % Atingimento, com as três faixas de cor e a linha TOTAL. Em 28/10/2026
  // ele pediu as duas colunas do mês aqui também — é no grupo que a líder lê.
  // Aqui ela sai pronta em JPEG, com os MESMOS números da tela e a mesma cara
  // do print dele, para ele baixar e encaminhar sem abrir o Excel.
  //
  // Desenhada no canvas de propósito: nada de biblioteca de "foto da tela", que
  // traz a fonte da marca, o tema escuro e o arredondado do painel para dentro
  // de uma imagem que vai para um grupo acostumado com a cara da planilha.
  //
  // ⚠ As cores são as do Excel dele (as faixas "Bom/Neutro/Ruim"), não as do
  // painel: é a mesma regra (≥100% verde · ≥80% amarelo · abaixo vermelho) com
  // a tinta que o grupo já reconhece.
  // ⚠ Pedidos da semana com UMA CASA, como no print dele (+8,2 / −6,9) — a tela
  // arredonda para inteiro, e aqui isso perderia a informação que ele mostra.
  // ⚠ JPEG não tem transparência: o fundo branco é pintado antes de tudo,
  // senão o que não for pintado sai preto.
  // ═════════════════════════════════════════════════════════════════════════
  const baixarImagem = async (s: Semana) => {
    setBaixandoImg(s.n)
    try {
      try { await document.fonts.ready } catch { /* fonte é detalhe, a tabela não */ }
      const ESC = 2                                    // 2× para não sair borrada
      // Código · Grupo · Pedidos da semana · % · Saldo de Pedidos.
      // ⚠ 03/11/2026: "Dif. Pedidos" virou "Pedidos da semana" na imagem e na
      // tela — é por ela que a líder se regula para o objetivo, e "Dif." não
      // dizia de que período era. O campo do backend continua `dif_pedidos`.
      // ⚠ Código e Grupo ENCOLHERAM (eram 300 e 470): a imagem é lida no celular
      // dentro do grupo, e cada ponto de largura a mais encolhe TODO o texto.
      // Sobrou folga ali, então a coluna nova saiu dela e a imagem continua com
      // os MESMOS 1200 de largura de sempre — a letra não diminuiu.
      // ⚠ "Adiantada / Atrasada" NÃO entra aqui (ele tirou em 28/10): fica na
      // tela e no Excel. A imagem é a cara da planilha do diretor, e lá essa
      // coluna não existe.
      const COL = [180, 420, 190, 210, 200]
      const X = COL.reduce<number[]>((a, w) => [...a, a[a.length - 1] + w], [0])
      const L = X[X.length - 1]                        // largura em ponto lógico
      const H_CAB = 64, H_LIN = 48, H_TOT = 58, H_PE = 38
      const linhas = s.linhas
      const alt = H_CAB + linhas.length * H_LIN + H_TOT + H_PE

      const cv = document.createElement('canvas')
      cv.width = L * ESC; cv.height = alt * ESC
      const g = cv.getContext('2d')
      if (!g) throw new Error('este navegador não desenha a imagem')
      g.scale(ESC, ESC)
      g.fillStyle = '#ffffff'; g.fillRect(0, 0, L, alt)
      g.textBaseline = 'middle'
      const FONTE_IMG = `"Segoe UI", system-ui, -apple-system, Roboto, Arial, sans-serif`
      const dir = (txt: string, x: number, y: number) => { g.textAlign = 'right'; g.fillText(txt, x, y) }
      const esq = (txt: string, x: number, y: number) => { g.textAlign = 'left'; g.fillText(txt, x, y) }

      // ── cabeçalho ───────────────────────────────────────────────────────
      g.fillStyle = '#1f4e79'; g.fillRect(0, 0, L, H_CAB)
      g.fillStyle = '#ffffff'; g.font = `600 25px ${FONTE_IMG}`
      dir('Código', X[1] - 24, H_CAB / 2)
      // "Grupo" centralizado sobre a coluna, como no print dele; os outros três
      // rótulos ficam à direita, em cima dos números que são à direita.
      g.textAlign = 'center'; g.fillText(geral ? 'Distrito' : 'Grupo', X[1] + COL[1] / 2, H_CAB / 2)
      dir('% Atingimento', X[4] - 18, H_CAB / 2)
      // Rótulos em duas linhas: os nomes são compridos e as colunas estreitas.
      // Quebrar em duas lê melhor que encolher a fonte.
      // ⚠ "Pedidos da semana" (era "Dif. Pedidos") entrou nesse mesmo desenho:
      // em uma linha ele não caberia nos 190 pontos da coluna.
      g.font = `600 22px ${FONTE_IMG}`
      dir('Pedidos da', X[3] - 18, H_CAB / 2 - 13)
      dir('semana', X[3] - 18, H_CAB / 2 + 13)
      dir('Saldo de', X[5] - 18, H_CAB / 2 - 13)
      dir('Pedidos', X[5] - 18, H_CAB / 2 + 13)
      g.font = `600 25px ${FONTE_IMG}`

      // ── uma linha por grupo ─────────────────────────────────────────────
      const faixa = (v: number) => (v >= 1 ? { fundo: '#c6efce', tinta: '#006100' }
        : v >= 0.8 ? { fundo: '#ffeb9c', tinta: '#9c6500' } : { fundo: '#ffc7ce', tinta: '#9c0006' })
      const umaCasa = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      let y = H_CAB
      for (let i = 0; i < linhas.length; i++) {
        const l = linhas[i]
        g.fillStyle = i % 2 ? '#f2f7fb' : '#ffffff'
        g.fillRect(0, y, L, H_LIN)
        const meio = y + H_LIN / 2
        g.font = `23px ${FONTE_IMG}`
        g.fillStyle = '#1a1a1a'
        dir(l.codigo, X[1] - 24, meio)
        esq((l.nome || l.codigo).toUpperCase(), X[1] + 24, meio)
        if (l.dif_pedidos != null) {
          g.fillStyle = l.dif_pedidos < 0 ? '#ff0000' : '#203864'
          dir(`${l.dif_pedidos > 0 ? '+' : l.dif_pedidos < 0 ? '−' : ''}${umaCasa(Math.abs(l.dif_pedidos))}`, X[3] - 18, meio)
        } else { g.fillStyle = '#9aa0a6'; dir('–', X[3] - 18, meio) }
        if (l.atingimento != null) {
          const f = faixa(l.atingimento)
          g.fillStyle = f.fundo; g.fillRect(X[3], y, COL[3], H_LIN)
          g.fillStyle = f.tinta; g.font = `600 23px ${FONTE_IMG}`
          dir(`${(l.atingimento * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`, X[4] - 18, meio)
        } else { g.fillStyle = '#9aa0a6'; g.font = `23px ${FONTE_IMG}`; dir('–', X[4] - 18, meio) }
        // ⚠ UMA CASA nas duas, como na planilha dele (89,6 / 62,2 / 28,7). Com
        // inteiro, +3,2 e −3,5 viram +3 e −4 e o saldo deixa de fechar aos
        // olhos de quem soma de cabeça — na tela há balão para explicar, numa
        // imagem encaminhada não há.
        g.font = `23px ${FONTE_IMG}`
        if (l.falta_fechar != null) {
          g.fillStyle = l.falta_fechar <= 0 ? '#006100' : '#1a1a1a'
          dir(l.falta_fechar <= 0 ? 'fechado' : umaCasa(l.falta_fechar), X[5] - 18, meio)
        } else { g.fillStyle = '#9aa0a6'; dir('–', X[5] - 18, meio) }
        g.strokeStyle = '#d9d9d9'; g.lineWidth = 1
        g.beginPath(); g.moveTo(0, y + H_LIN - 0.5); g.lineTo(L, y + H_LIN - 0.5); g.stroke()
        y += H_LIN
      }

      // ── TOTAL ───────────────────────────────────────────────────────────
      g.fillStyle = '#f2f2f2'; g.fillRect(0, y, L, H_TOT)
      g.strokeStyle = '#1f4e79'; g.lineWidth = 2.5
      g.beginPath(); g.moveTo(0, y + 1.25); g.lineTo(L, y + 1.25); g.stroke()
      const meioT = y + H_TOT / 2
      g.font = `700 25px ${FONTE_IMG}`; g.fillStyle = '#1a1a1a'
      esq('TOTAL', X[1] + 24, meioT)
      if (s.total.dif_pedidos != null) {
        g.fillStyle = s.total.dif_pedidos < 0 ? '#ff0000' : '#203864'
        dir(`${s.total.dif_pedidos > 0 ? '+' : s.total.dif_pedidos < 0 ? '−' : ''}${umaCasa(Math.abs(s.total.dif_pedidos))}`, X[3] - 18, meioT)
      }
      if (s.total.atingimento != null) {
        const f = faixa(s.total.atingimento)
        g.fillStyle = f.fundo; g.fillRect(X[3], y + 2.5, COL[3], H_TOT - 2.5)
        g.fillStyle = f.tinta
        dir(`${(s.total.atingimento * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`, X[4] - 18, meioT)
      }
      g.font = `700 25px ${FONTE_IMG}`
      if (s.total.falta_fechar != null) {
        g.fillStyle = s.total.falta_fechar <= 0 ? '#006100' : '#1a1a1a'
        dir(s.total.falta_fechar <= 0 ? 'fechado' : umaCasa(s.total.falta_fechar), X[5] - 18, meioT)
      }
      y += H_TOT

      // ── o pé: qual semana é esta ────────────────────────────────────────
      // Sem isso a imagem não diz de que semana é — e ela é encaminhada,
      // reencaminhada e olhada dias depois. Texto seco, sem assinatura de
      // ninguém: quem manda no grupo é ele.
      g.fillStyle = '#ffffff'; g.fillRect(0, y, L, H_PE)
      g.font = `20px ${FONTE_IMG}`; g.fillStyle = '#6b7280'
      const dt = new Date().toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
      // ⚠ No geral o pé TEM de dizer que é a distribuição inteira: a imagem é
      // encaminhada, e uma tabela de distritos sem isso parece a de um deles.
      esq(`${s.n}ª semana · ${sem(s.semana)} · vitrine ${dados!.vitrine}/${Math.floor(dados!.periodo / 100)}${geral ? ' · toda a distribuição' : ''}`, 24, y + H_PE / 2)
      g.textAlign = 'right'
      g.fillText(`${s.fonte === 'arquivo' ? 'relatório do site' : s.fonte === 'draco' ? 'apurado no Draco' : 'parcial'} · ${dt}`, L - 18, y + H_PE / 2)

      const nome = `Desafio-semana-${s.semana % 100}-${Math.floor(s.semana / 100)}.jpg`
      await new Promise<void>((pronto, falhou) => {
        cv.toBlob(blob => {
          if (!blob) return falhou(new Error('não consegui gerar a imagem'))
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url; a.download = nome
          document.body.appendChild(a); a.click(); a.remove()
          setTimeout(() => URL.revokeObjectURL(url), 2000)
          pronto()
        }, 'image/jpeg', 0.94)
      })
    } catch (e) { setErro((e as Error).message) } finally { setBaixandoImg(null) }
  }

  const opcoes = useMemo(() => (hoje ? opcoesPeriodo(hoje) : []), [hoje])
  const opcaoAberta = opcoes.find(o => o.semana === semanaSel) || null
  const tipoAberto: OpcaoPeriodo['tipo'] = opcaoAberta?.tipo || 'atual'
  const opcaoAtual = opcoes.find(o => o.tipo === 'atual') || null
  // Desde 31/10 as regras são POR VITRINE (o crm guarda em `vitrines[periodo]`):
  // salvar a 12 não mexe na 11. Sem regra própria, a vitrine usa as da anterior
  // mais próxima — `regras_de` diz qual, para a tela poder contar.
  const vitrineAberta = dados ? `${dados.vitrine}/${Math.floor(dados.periodo / 100)}` : ''
  const herdadas = dados && dados.regras_de !== dados.periodo
    ? (dados.regras_de ? `as mesmas da vitrine ${dados.regras_de % 100}/${Math.floor(dados.regras_de / 100)}` : 'as regras gerais do distrito')
    : null

  const cfg = rascunho || dados?.config || null
  const mudar = (f: (c: Config) => Config) => { if (cfg) setRascunho(f(JSON.parse(JSON.stringify(cfg)))) }
  const somaPesos = cfg ? cfg.pesos.reduce((t, p) => t + p, 0) : 1

  const salvar = async () => {
    if (!rascunho) { setEditando(false); return }
    setSalvando(true); setErro(null)
    try {
      await api('/api/desafio-planilha/config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        // ⚠ sempre com o distrito: sem ele o crm salvaria por cima da União.
        // ⚠ (31/10) `dados.distrito` NÃO serve aqui: durante a edição `dados` é a
        // resposta da /previa, que não traz o distrito — o Salvar de outro
        // distrito caía no default do crm e gravava por cima da União. O
        // distrito escolhido no seletor é o estado `distrito`.
        // `periodo`: as regras vão para a vitrine que está na tela (31/10).
        body: JSON.stringify({ config: rascunho, distrito: distrito || distritoAberto, periodo: dados?.periodo }),
      })
      await carregar()
      setVersao(v => v + 1)
    }
    catch (e) { setErro((e as Error).message) } finally { setSalvando(false) }
  }
  const cancelar = () => { setRascunho(null); setEditando(false); carregar() }

  // Desafio do mês definido pela diretoria: por grupo e o total do distrito,
  // os dois da vitrine na tela. null = volta para a regra.
  const setDesafio = (codigo: string, v: number | null) => mudar(c => {
    if (!dados) return c
    const p = String(dados.periodo)
    c.desafios = { ...(c.desafios || {}) }
    c.desafios[p] = { ...(c.desafios[p] || {}) }
    if (v == null) delete c.desafios[p][codigo]; else c.desafios[p][codigo] = v
    return c
  })
  const setTotal = (v: number | null) => mudar(c => {
    if (!dados) return c
    const p = String(dados.periodo)
    c.totais = { ...(c.totais || {}) }
    if (v == null) delete c.totais[p]; else c.totais[p] = v
    return c
  })
  // O ACRÉSCIMO em R$ do grupo (04/11/2026, pedido do dono: "só com % é ruim").
  // Caso que pediu: grupos baixados, consultoras passadas para o grupo BRASIL e
  // R$ 22.000 a mais no objetivo DELE. Com o campo "Desafio do mês" ela teria de
  // calcular média × (1 + incremento) + 22.000 à mão e o número congelaria;
  // aqui o grupo continua seguindo a regra e só carrega o acréscimo.
  const setAjuste = (codigo: string, v: number | null) => mudar(c => {
    if (!dados) return c
    const p = String(dados.periodo)
    c.ajustes = { ...(c.ajustes || {}) }
    c.ajustes[p] = { ...(c.ajustes[p] || {}) }
    if (v == null) delete c.ajustes[p][codigo]; else c.ajustes[p][codigo] = v
    return c
  })
  const totalDir = cfg && dados ? cfg.totais?.[String(dados.periodo)] ?? null : null
  const desafiosDir = cfg && dados ? cfg.desafios?.[String(dados.periodo)] || {} : {}
  const ajustesDir = cfg && dados ? cfg.ajustes?.[String(dados.periodo)] || {} : {}
  const nDiretoria = (dados?.grupos || []).filter(g => g.origem && g.origem !== 'regra').length
  const somaAjustes = (dados?.grupos || []).reduce((t, g) => t + (g.ajuste || 0), 0)
  const nAjustes = (dados?.grupos || []).filter(g => g.ajuste).length

  const setMedia = (codigo: string, v: number | null) => mudar(c => {
    if (!dados) return c
    const p = String(dados.periodo)
    c.medias[p] = { ...(c.medias[p] || {}) }
    if (v == null) delete c.medias[p][codigo]; else c.medias[p][codigo] = v
    return c
  })

  const grupoInfo = useMemo(() => new Map((dados?.grupos || []).map(g => [g.codigo, g])), [dados])

  const r = dados?.resumo
  // No geral a tela vira só leitura e a coluna "Grupo" vira "Distrito".
  const geral = !!dados?.geral
  const nDistritos = geral ? (dados?.semanas[0]?.linhas.length || 0) : 0
  const semanasComReal = r ? r.semanas.filter(s => s.realizado > 0).length : 0
  // A semana em andamento vem do relatório que o diretor envia, não do Draco —
  // o lote do Draco só entra na terça à noite. Dizer "no Draco" para todas é
  // mentira no cartão que ele mais olha, e é justamente a diferença que ele
  // precisa enxergar para saber o que já é apurado e o que ainda é prévia.
  const semanasDoArquivo = r ? r.semanas.filter(s => s.realizado > 0 && s.fonte === 'arquivo').length : 0
  const peRealizado = semanasDoArquivo
    ? `${semanasComReal} de 4 semanas · ${semanasComReal - semanasDoArquivo} no Draco, ${semanasDoArquivo} do relatório do site`
    : `${semanasComReal} de 4 semanas com venda no Draco`
  // ⚠ No geral o incremento NÃO é um número: ele varia de distrito para
  // distrito (hoje de 24% a 86%). Mostrar uma média ali seria inventar um
  // número que não existe em distrito nenhum — a faixa diz a verdade.
  const faixaPct = (f?: [number, number] | null) => (f ? `${pct(f[0], 0)} a ${pct(f[1], 0)}` : null)
  // ⚠ O acréscimo entra no total do mês: sem dizer isto no pé do cartão, o
  // número não fecha com "média dos grupos + incremento" e parece erro de conta.
  // ⚠ 04/11/2026: o incremento pode ser NEGATIVO (a Juliane salvou −13% na
  // vitrine 12 do BRASIL) e o pé do cartão saía "média dos grupos + -13%".
  // Em português claro: "menos 13%".
  const sobreMedia = (v: number) => (v < 0 ? `menos ${pct(Math.abs(v), 0)}` : `mais ${pct(v, 0)}`)
  const peAjuste = somaAjustes
    ? `, mais R$ ${inteiro(somaAjustes)} de acréscimo em ${nAjustes} grupo${nAjustes === 1 ? '' : 's'}`
    : ''
  const regras: { rot: string; valor: ReactNode; editar: ReactNode }[] = cfg ? [
    { rot: 'Incremento sobre a média', valor: cfg.incremento == null
        ? <span className="font-normal text-[var(--pln-tinta-3)]">varia por distrito · {faixaPct(dados?.variam?.incremento)}</span>
        : pct(cfg.incremento, 0), editar: <CampoNum valor={Math.round((cfg.incremento || 0) * 10000) / 100} onChange={v => mudar(c => ({ ...c, incremento: v / 100 }))} sufixo="%" largura="w-20" /> },
    ...cfg.pesos.map((p, k) => ({ rot: `Peso da ${SEMANA_NOME[k]}`, valor: pct(p, 0), editar: <CampoNum valor={Math.round(p * 10000) / 100} onChange={v => mudar(c => { c.pesos[k] = v / 100; return c })} sufixo="%" largura="w-20" /> })),
    { rot: 'Valor do pedido', valor: `R$ ${cfg.pedido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, editar: <CampoNum valor={cfg.pedido} onChange={v => mudar(c => ({ ...c, pedido: v }))} /> },
    { rot: 'Média mínima do grupo', valor: `R$ ${inteiro(cfg.piso)}`, editar: <CampoNum valor={cfg.piso} onChange={v => mudar(c => ({ ...c, piso: v }))} /> },
    ...(geral ? [] : [{
      rot: 'Desafio do distrito (diretoria)',
      valor: totalDir == null ? <span className="font-normal text-[var(--pln-tinta-3)]">pela regra</span> : brl(totalDir),
      editar: <CampoOpcional valor={totalDir} onChange={setTotal} dica={dados?.desafio_regra_total ? `regra: ${inteiro(dados.desafio_regra_total)}` : 'pela regra'} />,
    }]),
  ] : []

  return (
    <PanelShell
      titulo="Desafio"
      icone={<FiFlag />}
      sub={dados ? <>
        {dados.geral ? 'TODA A DISTRIBUIÇÃO' : (dados.distritos?.find(x => x.codigo === dados.distrito)?.nome || 'Distrito')} ·{' '}
        {opcoes.length ? (
          <select
            value={opcaoAberta?.chave || ''}
            onChange={e => { const o = opcoes.find(x => x.chave === e.target.value); if (o) { setSemanaSel(o.semana); setRascunho(null); setEditando(false) } }}
            disabled={editando || carregando}
            title={editando ? 'termine ou cancele a edição antes de trocar de vitrine' : 'escolha a vitrine do desafio'}
            className="rounded-md border border-border bg-transparent px-1.5 py-0.5 text-[inherit] font-semibold text-[var(--pln-tinta)] disabled:opacity-50 cursor-pointer"
          >
            {opcoes.map(o => <option key={o.chave} value={o.chave}>{rotuloPeriodo(o)}</option>)}
          </select>
        ) : <>vitrine {dados.vitrine}/{Math.floor(dados.periodo / 100)}</>}
        {' · '}
        {tipoAberto === 'atual' ? <>semana {sem(dados.semana_atual)}</>
          : <>semanas {dados.semanas[0] ? dados.semanas[0].semana % 100 : ''}–{dados.semanas[3] ? sem(dados.semanas[3].semana) : ''}</>}
        {dados.atualizado_por ? <> · regras ajustadas por {dados.atualizado_por}</> : null}
        {lidoEm ? <> · <span title="a tela relê sozinha quando você volta para esta aba">lido {new Date(lidoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span></> : null}
      </> : 'O desafio de venda por grupo, com o realizado do Draco'}
      toolbar={
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="pln-abas" role="tablist">
            <button role="tab" aria-selected={aba === 'desafio'} onClick={() => setAba('desafio')} className="pln-aba"><FiFlag /> Desafio</button>
            <button role="tab" aria-selected={aba === 'followup'} onClick={() => setAba('followup')} className="pln-aba"><FiList /> Follow Up</button>
          </div>
          {/* O distrito só muda a aba Desafio: o Follow Up é a distribuição
              inteira, de onde sai o realizado de todos eles. */}
          {aba === 'desafio' && dados?.distritos?.length ? (
            <label className="flex items-center gap-2 text-[13px] text-[var(--pln-tinta-2)]">
              <FiMapPin className="text-[var(--pln-tinta-3)]" /> Distrito
              <select
                value={dados.distrito || ''}
                onChange={e => { setDistrito(e.target.value); setRascunho(null); setEditando(false) }}
                disabled={editando}
                title={editando ? 'termine ou cancele a edição das regras antes de trocar de distrito' : 'escolha o distrito do desafio'}
                className="neu-btn rounded-lg border border-border bg-transparent px-2.5 py-1.5 text-[13px] font-semibold text-[var(--pln-tinta)] disabled:opacity-50"
              >
                {/* TODA A DISTRIBUIÇÃO (sugestão da Juliane Novais, 03/11/2026):
                    o geral em primeiro, antes dos distritos. */}
                <option value="geral">TODA A DISTRIBUIÇÃO · {dados.distritos.filter(x => x.tem_desafio).length} distritos</option>
                {dados.distritos.map(x => (
                  <option key={x.codigo} value={x.codigo}>
                    {x.nome} · {x.grupos} grupo{x.grupos === 1 ? '' : 's'}{x.tem_desafio ? '' : ' · sem desafio'}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      }
      acoes={aba === 'followup' ? null : (
        <div className="flex items-center gap-2">
          {editando ? (
            <>
              <button onClick={cancelar} className={BTN}><FiX /> Cancelar</button>
              <button onClick={salvar} disabled={salvando || Math.abs(somaPesos - 1) > 0.0001} className={BTN_PRI}>
                {salvando ? <FiLoader className="animate-spin" /> : <FiCheck />} Salvar
              </button>
            </>
          ) : (
            <>
              <button onClick={baixarExcel} disabled={baixando || !dados} className={BTN}>{baixando ? <FiLoader className="animate-spin" /> : <FiDownload />} Excel</button>
              <button onClick={carregar} disabled={carregando} className={BTN}>{carregando ? <FiLoader className="animate-spin" /> : <FiRefreshCw />} Atualizar</button>
              {/* ⚠ No geral não há o que editar: as regras são de cada distrito,
                  e o PUT /config exige um. (Já houve um Salvar sem distrito que
                  gravou por cima da União.) */}
              <button onClick={() => setEditando(true)} disabled={!dados || geral} className={BTN}
                title={geral ? 'o geral é só leitura: escolha um distrito para editar as regras dele' : undefined}><FiEdit2 /> Editar</button>
            </>
          )}
        </div>
      )}
    >
      <div className="flex-1 min-h-0 overflow-auto pb-8 flex flex-col gap-4">
        {aba === 'followup' && <AbaFollowUp api={api} aoMudarRealizado={recarregarRealizado} />}
        {aba === 'desafio' && erro && <div className="rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-950/40 dark:border-rose-900 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">{erro}</div>}
        {aba === 'desafio' && !dados && carregando && <div className="pln-vazio inline-flex items-center gap-2 p-6"><FiLoader className="animate-spin" /> Lendo o Draco…</div>}
        {aba === 'desafio' && dados?.proposta && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900 px-4 py-3 text-[13px] text-amber-800 dark:text-amber-200 flex gap-2.5">
            <FiAlertTriangle className="shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <strong className="font-semibold">Ainda não há desafio salvo para este distrito.</strong>{' '}
              O que está abaixo é uma <strong>proposta</strong>: as mesmas regras do União, os
              {' '}{dados.grupos.length} grupos que venderam na vitrine e a <strong>média vinda do
              Draco</strong> (nenhum número foi digitado). Confira, ajuste o que precisar e clique em
              {' '}<strong>Salvar</strong> — enquanto não salvar, nada disso está valendo.
            </div>
          </div>
        )}
        {aba === 'desafio' && dados?.geral && (
          <div className="rounded-xl border border-teal-200 bg-teal-50 dark:bg-teal-950/40 dark:border-teal-900 px-4 py-3 text-[13px] text-teal-900 dark:text-teal-200 flex gap-2.5">
            <FiMapPin className="shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <strong className="font-semibold">Toda a distribuição</strong>: a soma dos{' '}
              {nDistritos} distritos com desafio salvo — <strong>cada linha é um distrito</strong>,
              com o total dele. Só leitura: para mexer nas regras, <strong>clique no nome do
              distrito</strong> na tabela (ou escolha no seletor) — a vitrine aberta é mantida.
              {dados.fora && dados.fora.length ? <> ⚠ Fora da soma: <strong>{dados.fora.map(d => d.nome).join(', ')}</strong> (ainda sem desafio salvo).</> : null}
            </div>
          </div>
        )}
        {aba === 'desafio' && dados && tipoAberto !== 'atual' && (
          <div className="rounded-xl border border-sky-200 bg-sky-50 dark:bg-sky-950/40 dark:border-sky-900 px-4 py-3 text-[13px] text-sky-900 dark:text-sky-200 flex gap-2.5">
            <FiAlertTriangle className="shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              {tipoAberto === 'proxima' ? <>
                <strong className="font-semibold">Vitrine {dados.vitrine}/{Math.floor(dados.periodo / 100)} ainda não começou</strong>: aqui está só o objetivo, sem realizado.
                {' '}A média de cada grupo vem das duas vitrines anteriores
                {opcaoAtual && !ultimaFechada ? <>; a semana {sem(opcaoAtual.ultima)} ainda vem do <strong>relatório do site</strong> que você enviou, e muda quando o lote entrar no Draco (terça à noite)</> : null}.
                {' '}Em <strong>Editar</strong>, regras, médias e o desafio do mês da diretoria valem só para esta vitrine.
              </> : <>
                <strong className="font-semibold">Vitrine encerrada.</strong>{' '}
                {herdadas ? <>Ela não tem regra própria salva: a conta usa {herdadas}, então o objetivo daquela época pode ter sido diferente.</> : <>Conta com as regras salvas para ela.</>}
              </>}
            </div>
          </div>
        )}
        {aba === 'desafio' && dados && cfg && r && (
          <>
            <PlnKpis>
              <PlnKpi rotulo="Desafio do mês" valor={brl(r.total.desafio)} pe={geral
                ? `a soma dos ${nDistritos} distritos`
                : totalDir != null
                ? 'definido pela diretoria para o distrito'
                : nDiretoria ? `${nDiretoria} grupo${nDiretoria === 1 ? '' : 's'} com valor da diretoria, o resto média ${sobreMedia(cfg.incremento || 0)}${peAjuste}`
                : `média dos grupos ${sobreMedia(cfg.incremento || 0)}${peAjuste}`} />
              <PlnKpi rotulo="Realizado até agora" valor={brl(r.total.realizado)} pe={peRealizado} />
              <PlnKpi rotulo="Atingimento acumulado" valor={pct(r.acumulado)} tom={r.acumulado == null ? undefined : r.acumulado >= 1 ? 'ok' : r.acumulado < 0.8 ? 'ruim' : undefined} pe="nas semanas que já têm realizado" />
              <PlnKpi rotulo="Pedidos sobre a meta" valor={dif(r.total.dif_pedidos)} pe={`pedidos de R$ ${inteiro(cfg.pedido)} acima (+) ou abaixo (−)`} />
            </PlnKpis>

            {!editando && <GraficoEvolucao api={api} distrito={distrito || distritoAberto} periodoAberto={dados.periodo} versao={versao} geral={geral} />}

            <div className="grid gap-4 lg:grid-cols-[minmax(300px,380px)_1fr] items-start">
              <PlnBloco titulo="Regras do desafio" nota={editando ? 'os pesos precisam somar 100%' : undefined}>
                <div className="overflow-x-auto">
                <table className="pln-tabela">
                  <tbody>
                    {regras.map(x => (
                      <tr key={x.rot}><td>{x.rot}</td><td className={n}>{editando ? x.editar : <strong className="font-semibold">{x.valor}</strong>}</td></tr>
                    ))}
                    <tr><td colSpan={2} className="text-[12.5px] text-[var(--pln-tinta-3)] leading-snug">
                      {/* ⚠ No geral não há "regra própria da vitrine": cada distrito
                          tem a dele. A nota de herança aqui seria mentira. */}
                      {geral
                        ? <>Cada distrito tem as regras dele; aqui aparece o que é <strong className="font-semibold">igual nos {nDistritos}</strong>. O que varia vira faixa.</>
                        : editando
                        ? <>Vale só para a vitrine {vitrineAberta}: salvar aqui não muda as outras.</>
                        : herdadas ? <>Vitrine {vitrineAberta} ainda sem regra própria: está usando {herdadas}.</> : <>Regras da vitrine {vitrineAberta}.</>}
                    </td></tr>
                    {editando && Math.abs(somaPesos - 1) > 0.0001 && (
                      <tr><td colSpan={2} className="pln-ruim text-[13px]">Os pesos somam {pct(somaPesos, 0)}: ajuste para dar 100%.</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              </PlnBloco>

              <PlnBloco titulo="Resumo do mês" nota={`vitrine ${dados.vitrine}`}>
                <div className="overflow-x-auto">
                  <table className="pln-tabela">
                    <thead><tr><th className="text-left">Semana</th><th className={n}>% do mês</th><th className={n}>Desafio</th><th className={n}>Realizado</th><th className={n}>Atingimento</th><th className={n} title="Pedidos a mais (+) ou a menos (−) que a meta DESTA semana — o que falta no mês inteiro é a coluna &quot;Saldo de Pedidos&quot;">Pedidos da semana</th></tr></thead>
                    <tbody>
                      {r.semanas.map(s => (
                        <tr key={s.n}>
                          <td>{SEMANA_NOME[s.n - 1]} <span className="text-[var(--pln-tinta-3)] text-[12.5px] ml-1">{sem(s.semana)}</span></td>
                          <td className={n} data-rot="% do mês">{pct(s.peso, 0)}</td>
                          <td className={n} data-rot="Desafio">{brl(s.desafio)}</td>
                          <td className={n} data-rot="Realizado">{brl(s.realizado)}</td>
                          <td className={n} data-rot="Atingimento"><Selo v={s.atingimento} /></td>
                          <td className={n} data-rot="Pedidos da semana">{dif(s.dif_pedidos)}</td>
                        </tr>
                      ))}
                      <tr className="pln-linha-forte">
                        <td>Total do mês</td>
                        <td className={n} data-rot="% do mês">{pct(r.total.peso, 0)}</td>
                        <td className={n} data-rot="Desafio">{brl(r.total.desafio)}</td>
                        <td className={n} data-rot="Realizado">{brl(r.total.realizado)}</td>
                        <td className={n} data-rot="Atingimento"><Selo v={r.total.atingimento} /></td>
                        <td className={n} data-rot="Pedidos da semana">{dif(r.total.dif_pedidos)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </PlnBloco>
            </div>

            {editando && (
              <PlnBloco
                titulo="Grupos, médias e objetivo"
                nota={`a média de venda de cada grupo na vitrine ${dados.vitrine} · o Draco sugere a média das 2 vitrines anteriores · "Desafio do mês" vazio = pela regra · "Somar" acrescenta R$ por cima dela`}
                extra={<button onClick={() => setEscolher(true)} className={BTN}><FiPlus /> Adicionar grupo</button>}
              >
                <div className="overflow-x-auto">
                  <table className="pln-tabela">
                    <thead><tr><th className="text-left">Grupo</th><th className={n}>Média de venda</th><th className={n}>Sugestão do Draco</th><th className={n} title="SUBSTITUI a conta deste grupo. Vazio = média + incremento (a regra). Para só acrescentar um valor, use a coluna ao lado.">Desafio do mês</th><th className={n} title="SOMA ao desafio do grupo, sem tirar ele da regra: o acréscimo continua valendo se a média ou o incremento mudarem. Use quando um grupo recebe consultoras de outro.">Somar (R$)</th><th /></tr></thead>
                    <tbody>
                      {(dados.semanas[0]?.linhas || []).map(l => {
                        const g = grupoInfo.get(l.codigo)
                        const igual = g ? Math.abs(g.media - g.media_draco) < 0.005 : true
                        return (
                          <tr key={l.codigo}>
                            <td>{l.nome || l.codigo} <span className="text-[var(--pln-tinta-3)] text-[12.5px] ml-1">{l.codigo}</span></td>
                            <td className={n} data-rot="Média de venda"><CampoNum valor={Math.round(l.media * 100) / 100} onChange={v => setMedia(l.codigo, v)} largura="w-32" /></td>
                            <td className={n} data-rot="Sugestão do Draco">
                              {g && (igual ? <span className="text-[var(--pln-tinta-3)]">igual</span>
                                : <button onClick={() => setMedia(l.codigo, null)} className="underline text-[var(--pln-verde)]">usar {brl(g.media_draco)}</button>)}
                            </td>
                            <td className={n} data-rot="Desafio do mês">
                              <CampoOpcional valor={desafiosDir[l.codigo]} onChange={v => setDesafio(l.codigo, v)}
                                dica={g?.origem === 'total' && g.base_mes != null ? `total: ${inteiro(g.base_mes)}` : `regra: ${inteiro(g?.desafio_regra ?? l.media * (1 + (cfg.incremento || 0)))}`} />
                            </td>
                            {/* O acréscimo, e embaixo em quanto o objetivo do grupo FICA — ela
                                pediu "adicionar 22.000" e não tem de somar de cabeça para
                                conferir. O número vem da prévia, que recalcula no crm meio
                                segundo depois de parar de digitar. */}
                            <td className={n} data-rot="Somar (R$)">
                              <CampoOpcional valor={ajustesDir[l.codigo]} onChange={v => setAjuste(l.codigo, v)} dica="0" largura="w-28" />
                              {!!ajustesDir[l.codigo] && g?.desafio_mes != null && (
                                <div className="text-[11.5px] text-[var(--pln-tinta-3)] mt-0.5">fica {brl(g.desafio_mes)}</div>
                              )}
                            </td>
                            <td className="text-right">
                              {/* ⚠ O objetivo do grupo vai no title do botão (04/11/2026): grupo
                                  baixado costuma ter o objetivo PASSADO para quem recebeu as
                                  consultoras, e sem o número aqui ela tira a linha e perde de
                                  vista quanto tinha de remanejar. */}
                              <button
                                title={g?.desafio_mes != null
                                  ? `Tirar do desafio — hoje ele carrega R$ ${inteiro(g.desafio_mes)}. Se as consultoras foram para outro grupo, some esse valor na coluna "Somar (R$)" dele antes de tirar.`
                                  : 'Tirar do desafio'}
                                onClick={() => mudar(c => ({ ...c, grupos: c.grupos.filter(x => x !== l.codigo) }))}
                                className="text-[var(--pln-tinta-3)] hover:text-[var(--pln-ruim)] p-1"><FiTrash2 /></button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </PlnBloco>
            )}

            {dados.semanas.map(s => {
              const maxReal = Math.max(0, ...s.linhas.map(l => l.realizado || 0))
              const semReal = s.total.realizado == null
              return (
                <PlnBloco
                  key={s.n}
                  titulo={<>{SEMANA_NOME[s.n - 1]} <span className="font-normal text-[var(--pln-tinta-3)]">· {sem(s.semana)}</span></>}
                  nota={`${pct(s.peso, 0)} do desafio do mês · ${ESTADO[s.estado]}${FONTE[s.fonte || ''] || (semReal && s.estado === 'andamento' ? ' · falta enviar o relatório do site (aba Follow Up)' : '')}`}
                  extra={
                    <span className="inline-flex items-center gap-2.5 text-[13px] text-[var(--pln-tinta-2)]">
                      Atingimento <Selo v={s.total.atingimento} />
                      <button
                        onClick={() => baixarImagem(s)}
                        disabled={baixandoImg === s.n || s.total.atingimento == null}
                        className={BTN}
                        title={s.total.atingimento == null ? 'esta semana ainda não tem realizado' : 'baixar a imagem desta semana para mandar no grupo'}
                      >
                        {baixandoImg === s.n ? <FiLoader className="animate-spin" /> : <FiImage />} Imagem
                      </button>
                    </span>
                  }
                >
                  <div className="overflow-x-auto">
                    <table className="pln-tabela [&_td]:py-2.5">
                      <thead>
                        <tr>
                          {/* ⚠ "Média vendas" não aparece no geral: do lado do
                              desafio, a soma das médias era lida como se fosse
                              ele. Num distrito ela fica — é a base da conta. */}
                          <th className="text-left">{geral ? 'Distrito' : 'Grupo'}</th>
                          {!geral && <th className={n}>Média vendas</th>}
                          <th className={n} title={geral ? 'o quanto este distrito pesa na distribuição, pela média de venda dos grupos dele' : undefined}>Peso</th><th className={n}>Desafio mês</th>
                          <th className={n}>Desafio semana</th><th className={n}>Pedidos meta</th><th className={n}>Realizado</th>
                          <th className={n}>Pedidos feitos</th><th className={n} title="Pedidos a mais (+) ou a menos (−) que a meta DESTA semana — o que falta no mês inteiro é a coluna &quot;Saldo de Pedidos&quot;">Pedidos da semana</th><th className={n}>Atingimento</th>
                          {/* Persistência do resultado (28/10/2026): a tabela mostrava só o
                              resultado DA SEMANA, sem ninguém ver quanto ainda falta para
                              fechar o mês. Mesmo nome e mesmos números da planilha do
                              diretor — conferido grupo a grupo, 16 de 16, em 28/10.
                              ⚠ Houve também uma coluna "Adiantada / Atrasada" (o saldo
                              acumulado); ele mandou tirar no mesmo dia, da imagem e da
                              tela. O campo `saldo_mes` continua vindo do backend, com
                              teste, caso ele queira de volta. */}
                          <th className={n} title="Quantos pedidos ainda faltam para bater o objetivo do mês (o &quot;Saldo de Pedidos&quot; da planilha)">Saldo de Pedidos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.linhas.map(l => (
                          <tr key={l.codigo}>
                            {/* No geral a linha é um DISTRITO e dá para ABRIR:
                                o Editar fica desligado ali, então quem quer
                                mexer NESTA linha precisa de um caminho até ela.
                                A vitrine aberta é mantida. */}
                            <td className="whitespace-nowrap">
                              {geral ? (
                                <button
                                  onClick={() => { setDistrito(l.codigo); setRascunho(null); setEditando(false) }}
                                  className="text-left hover:underline decoration-dotted underline-offset-4"
                                  title={`abrir ${l.nome || l.codigo} para ver os grupos e editar as regras`}
                                >{l.nome || l.codigo}</button>
                              ) : (l.nome || l.codigo)}
                              <span className="text-[var(--pln-tinta-3)] text-[12px] ml-1">{l.codigo}{l.grupos != null ? ` · ${l.grupos} grupo${l.grupos === 1 ? '' : 's'}` : ''}</span><EtiquetaOrigem o={l.origem} /><EtiquetaAjuste v={l.ajuste} /></td>
                            {!geral && <td className={n} data-rot="Média vendas">{brl(l.media)}</td>}
                            <td className={`${n} text-[var(--pln-tinta-3)]`} data-rot="Peso">{pct(l.peso_grupo)}</td>
                            <td className={n} data-rot="Desafio mês">{brl(l.desafio_mes)}</td>
                            <td className={n} data-rot="Desafio semana">{brl(l.desafio_semana)}</td>
                            <td className={n} data-rot="Pedidos meta">{qtd(l.pedidos_meta)}</td>
                            <td className={n} data-rot="Realizado">
                              <div>{brl(l.realizado)}</div>
                              {!!l.realizado && maxReal > 0 && <div className="mt-1 h-1 rounded-full bg-[var(--pln-fio)] overflow-hidden"><div className="h-full rounded-full bg-[var(--pln-verde-vivo)] opacity-70 ml-auto" style={{ width: `${(l.realizado / maxReal) * 100}%` }} /></div>}
                            </td>
                            <td className={n} data-rot="Pedidos feitos">{qtd(l.pedidos_realizados)}</td>
                            <td className={n} data-rot="Pedidos da semana">{dif(l.dif_pedidos)}</td>
                            <td className={n} data-rot="Atingimento"><Selo v={l.atingimento} /></td>
                            <td className={n} data-rot="Saldo de Pedidos" title={l.falta_fechar == null ? undefined : `faltam ${comCasa(l.falta_fechar)} de ${comCasa(l.pedidos_mes)} pedidos do mês`}>{falta(l.falta_fechar)}</td>
                          </tr>
                        ))}
                        <tr className="pln-linha-forte">
                          <td>Total</td>
                          {!geral && <td className={n} data-rot="Média vendas">{brl(s.total.media)}</td>}
                          <td className={`${n} text-[var(--pln-tinta-3)]`} data-rot="Peso">{pct(s.total.peso_grupo)}</td>
                          <td className={n} data-rot="Desafio mês">{brl(s.total.desafio_mes)}</td>
                          <td className={n} data-rot="Desafio semana">{brl(s.total.desafio_semana)}</td>
                          <td className={n} data-rot="Pedidos meta">{qtd(s.total.pedidos_meta)}</td>
                          <td className={n} data-rot="Realizado">{brl(s.total.realizado)}</td>
                          <td className={n} data-rot="Pedidos feitos">{qtd(s.total.pedidos_realizados)}</td>
                          <td className={n} data-rot="Pedidos da semana">{dif(s.total.dif_pedidos)}</td>
                          <td className={n} data-rot="Atingimento"><Selo v={s.total.atingimento} /></td>
                          <td className={n} data-rot="Saldo de Pedidos" title={s.total.falta_fechar == null ? undefined : `faltam ${comCasa(s.total.falta_fechar)} de ${comCasa(s.total.pedidos_mes)} pedidos do mês`}>{falta(s.total.falta_fechar)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </PlnBloco>
              )
            })}
            <p className="text-[12.5px] text-[var(--pln-tinta-3)] px-1 leading-relaxed">
              Realizado = venda da semana (o mesmo “Q$” do Follow Up). Semana já apurada vem do <strong>Draco</strong>;
              a que ainda não fechou vem do <strong>relatório do site</strong> que você envia na aba Follow Up. O lote da
              semana entra no Draco na noite de segunda ou terça, e aí o apurado assume sozinho.
            </p>
          </>
        )}
      </div>
      {escolher && cfg && <EscolherGrupos api={api} distrito={dados?.distrito} atuais={cfg.grupos} fechar={() => setEscolher(false)} adicionar={cod => mudar(c => ({ ...c, grupos: c.grupos.includes(cod) ? c.grupos : [...c.grupos, cod] }))} />}
    </PanelShell>
  )
}


// ═══════════════════════════════════════════════════════════════════════════
// ENVIAR O ARQUIVO DO SITE — o que o diretor pediu em 27/10 ("subir aquele
// relatório que tem na segunda aba").
//
// Ele arrasta o Follow Up que baixou do site. A tela NÃO interpreta nada: só
// vira o arquivo em matriz com o SheetJS e manda para o crm, que lê, confere
// contra o total do próprio relatório e contra o Draco, e só então grava — e
// SÓ nas semanas que o Draco ainda não fechou. Semana apurada não se mexe.
//
// ⚠ Entre ler e gravar tem o CONFERIR: ele vê o que entrou (semanas, grupos,
// quanto deu) e o quanto o arquivo bate com o apurado nas semanas fechadas,
// ANTES de o número entrar no desafio. Arquivo trocado não vira número errado.
// ═══════════════════════════════════════════════════════════════════════════
const quando = (s: string | null) => {
  if (!s) return null
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T') + (/Z|[+-]\d\d:?\d\d$/.test(s) ? '' : 'Z'))
  return Number.isNaN(d.getTime()) ? null
    : d.toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function EnviarFollowUp({ api, envios, aoEnviar }: { api: ApiFn; envios: Envio[]; aoEnviar: () => void }) {
  const [arrastando, setArrastando] = useState(false)
  const [lendo, setLendo] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [conf, setConf] = useState<Conferido | null>(null)
  const [matriz, setMatriz] = useState<unknown[][] | null>(null)
  const [nome, setNome] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const limpar = () => { setConf(null); setMatriz(null); setNome(''); setErro(null) }

  const ler = async (f: File) => {
    limpar(); setLendo(true); setNome(f.name)
    try {
      const XLSX = await import('xlsx')
      const wb = XLSX.read(new Uint8Array(await f.arrayBuffer()), { type: 'array' })
      // O arquivo do site costuma ter uma aba só, mas a planilha dele tem duas:
      // vale a primeira aba que PAREÇA o Follow Up (tem célula "SS/AAAA").
      let m: unknown[][] | null = null
      for (const aba of wb.SheetNames) {
        const linhas = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[aba], { header: 1, raw: true, blankrows: true })
        if (linhas.some(l => (l || []).some(c => /^\d{1,2}\/\d{4}$/.test(String(c ?? '').trim())))) { m = linhas; break }
      }
      if (!m) throw new Error('não achei o Follow Up em nenhuma aba do arquivo (nenhuma célula como "41/2026"). Esse é mesmo o relatório do site?')
      setMatriz(m)
      const d = await api('/api/desafio-planilha/followup/conferir', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matriz: m, arquivo: f.name }),
      })
      setConf(d as unknown as Conferido)
    } catch (e) { setErro((e as Error).message); setMatriz(null) } finally { setLendo(false) }
  }

  const enviar = async () => {
    if (!matriz) return
    setEnviando(true); setErro(null)
    try {
      await api('/api/desafio-planilha/followup', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matriz, arquivo: nome }),
      })
      limpar(); aoEnviar()
    } catch (e) { setErro((e as Error).message) } finally { setEnviando(false) }
  }

  const tirar = async (semana: number) => {
    setEnviando(true); setErro(null)
    try { await api(`/api/desafio-planilha/followup/${semana}`, { method: 'DELETE' }); aoEnviar() }
    catch (e) { setErro((e as Error).message) } finally { setEnviando(false) }
  }

  const entram = conf ? conf.semanas.filter(s => s.entra) : []
  const naoConfere = conf ? conf.semanas.filter(s => s.confere === false) : []

  return (
    <PlnBloco
      titulo="Relatório do site"
      nota="o Follow Up que você baixa do site de pedidos — é dele que sai o realizado da semana que ainda não fechou"
    >
      <div className="p-4 flex flex-col gap-3">
        {envios.map(e => (
          <div key={e.semana} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-[var(--pln-borda)] bg-[var(--pln-chao)] px-3.5 py-2.5 text-[13px]">
            <FiCheck className="text-[var(--pln-verde)]" />
            <strong className="font-semibold">Semana {sem(e.semana)}</strong>
            <span>{brl(e.venda)} · {e.novas} novas · {e.grupos} grupos</span>
            <span className="text-[var(--pln-tinta-3)]">
              {e.arquivo ? `${e.arquivo} · ` : ''}enviado{e.por ? ` por ${e.por}` : ''}{quando(e.em) ? ` em ${quando(e.em)}` : ''}
            </span>
            <button onClick={() => tirar(e.semana)} disabled={enviando} className="ml-auto text-[var(--pln-tinta-3)] hover:text-[var(--pln-ruim)] p-1" title="Tirar este arquivo do desafio"><FiTrash2 /></button>
            <div className="w-full text-[12.5px] text-[var(--pln-verde)]">
              O desafio já está usando este arquivo no realizado da semana {sem(e.semana)} — veja na aba <strong>Desafio</strong>.
            </div>
          </div>
        ))}

        {!conf && (
          <label
            onDragOver={ev => { ev.preventDefault(); setArrastando(true) }}
            onDragLeave={() => setArrastando(false)}
            onDrop={ev => { ev.preventDefault(); setArrastando(false); const f = ev.dataTransfer.files?.[0]; if (f) ler(f) }}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-7 text-center cursor-pointer transition
              ${arrastando ? 'border-[var(--pln-verde-vivo)] bg-[var(--pln-ciano-claro)]' : 'border-[var(--pln-borda)] hover:bg-[var(--pln-chao)]'}`}
          >
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
              onChange={ev => { const f = ev.target.files?.[0]; if (f) ler(f); ev.target.value = '' }} />
            {lendo ? <FiLoader className="w-6 h-6 animate-spin text-[var(--pln-verde)]" /> : <FiUploadCloud className="w-6 h-6 text-[var(--pln-verde)]" />}
            <span className="text-[14px] font-semibold">{lendo ? `Lendo ${nome}…` : 'Arraste aqui o relatório do site'}</span>
            <span className="text-[12.5px] text-[var(--pln-tinta-3)]">ou toque para escolher o arquivo · .xlsx, .xls ou .csv</span>
          </label>
        )}

        {erro && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-950/40 dark:border-rose-900 px-4 py-3 text-sm text-rose-700 dark:text-rose-300 flex gap-2">
            <FiAlertTriangle className="shrink-0 mt-0.5" /><div>{erro}</div>
          </div>
        )}

        {conf && (
          <div className="flex flex-col gap-3">
            <div className="text-[13px]">
              <strong className="font-semibold">{nome}</strong>
              <span className="text-[var(--pln-tinta-3)]">
                {conf.cabecalho.distribuicao ? ` · ${conf.cabecalho.distribuicao}` : ''}
                {conf.cabecalho.previa ? ' · Prévia' : ''} · {conf.grupos} grupos em {conf.distritos.length} distritos
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="pln-tabela">
                <thead><tr><th className="text-left">Semana</th><th className={n}>Venda no arquivo</th><th className={n}>Novas</th><th className="text-left">O que vai acontecer</th></tr></thead>
                <tbody>
                  {conf.semanas.map(s => (
                    <tr key={s.semana}>
                      <td className="whitespace-nowrap font-semibold">{sem(s.semana)}</td>
                      <td className={n} data-rot="Venda no arquivo">{brl(s.venda)}</td>
                      <td className={n} data-rot="Novas">{qtd(s.novas)}</td>
                      <td data-rot="O que vai acontecer" className="text-[13px]">
                        {s.entra
                          ? <span className="text-[var(--pln-verde)] font-semibold">entra no desafio (o Draco ainda não fechou esta semana)</span>
                          : s.confere
                            ? <span className="text-[var(--pln-tinta-3)]">já apurada no Draco e <strong className="text-[var(--pln-verde)]">bate com o arquivo</strong> — fica como está</span>
                            : <span className="pln-ruim">já apurada no Draco, que diz {brl(s.draco?.venda)} e {qtd(s.draco?.novas)} novas — fica o Draco</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {naoConfere.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900 px-4 py-3 text-[13px] text-amber-800 dark:text-amber-200 flex gap-2">
                <FiAlertTriangle className="shrink-0 mt-0.5" />
                <div>Em {naoConfere.length === 1 ? 'uma semana já apurada' : `${naoConfere.length} semanas já apuradas`} o arquivo não bate com o Draco. Não impede o envio — essas semanas não são tocadas —, mas vale conferir se é o relatório certo.</div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={enviar} disabled={enviando || !entram.length} className={BTN_PRI}>
                {enviando ? <FiLoader className="animate-spin" /> : <FiCheck />}
                {entram.length ? `Usar no desafio (${entram.map(s => sem(s.semana)).join(', ')})` : 'Nada para completar'}
              </button>
              <button onClick={limpar} disabled={enviando} className={BTN}><FiX /> Escolher outro</button>
              {!entram.length && (
                <span className="text-[12.5px] text-[var(--pln-tinta-3)]">
                  Todas as semanas do arquivo já fecharam no Draco — o apurado manda, não há o que completar.
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </PlnBloco>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// ABA FOLLOW UP — a 2ª aba da planilha, o relatório do ERP.
//
// A leitura vem em DUAS CAMADAS, porque são 118 grupos: a tabela abre com os 10
// DISTRITOS (dá para ler no celular) e cada linha de distrito abre os grupos
// dela, uma camada abaixo (fundo de chão, código recuado). O que cada coluna
// mede vai ESCRITO embaixo da tabela — "Venda" e "Novas" são o Q$ e o F do ERP.
// ═══════════════════════════════════════════════════════════════════════════
function AbaFollowUp({ api, aoMudarRealizado }: { api: ApiFn; aoMudarRealizado: () => void }) {
  const [dados, setDados] = useState<FollowUp | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  const [baixando, setBaixando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try { setDados(await api('/api/desafio-planilha/follow-up') as unknown as FollowUp) }
    catch (e) { setErro((e as Error).message) } finally { setCarregando(false) }
  }, [api])
  useEffect(() => { carregar() }, [carregar])

  const vira = (cod: string) => setAbertos(s => { const n = new Set(s); n.has(cod) ? n.delete(cod) : n.add(cod); return n })
  // "Com número" = fechada no Draco OU completada pelo arquivo do site.
  const semanasComNumero = dados ? dados.semanas.filter(s => s.tem_lote) : []
  const ultima = semanasComNumero.length ? semanasComNumero[semanasComNumero.length - 1] : null
  const vendendo = dados && ultima ? dados.vendendo[ultima.n - 1] : null
  const rot = (dados?.semanas || []).map(s => sem(s.semana))

  // O Excel sai na ORDEM DA TELA, com os distritos e os grupos — é a aba dele.
  const baixar = async () => {
    if (!dados) return
    setBaixando(true)
    try {
      const XLSX = await import('xlsx')
      const cab1 = ['Distrito', 'Código', 'Grupo', ...dados.semanas.flatMap(s => [sem(s.semana), '']), 'Total', '']
      const cab2 = ['', '', '', ...dados.semanas.flatMap(() => ['Venda', 'Novas']), 'Venda', 'Novas']
      const cel = (c: FuCel) => [c.qs == null ? '' : c.qs, c.f == null ? '' : c.f]
      const aoa: (string | number)[][] = [cab1, cab2]
      for (const d of dados.distritos) {
        aoa.push([d.nome || d.codigo, d.codigo, 'TOTAL DO DISTRITO', ...d.semanas.flatMap(cel), ...cel(d.total)] as (string | number)[])
        for (const g of d.grupos) aoa.push([d.nome || d.codigo, g.codigo, g.nome || g.codigo, ...g.semanas.flatMap(cel), ...cel(g.total)] as (string | number)[])
      }
      aoa.push(['TOTAL', '', 'CAMPO GRANDE', ...dados.total.semanas.flatMap(cel), ...cel(dados.total.total)] as (string | number)[])
      const ws = XLSX.utils.aoa_to_sheet(aoa)
      ws['!cols'] = [{ wch: 18 }, { wch: 9 }, { wch: 30 }, ...dados.semanas.flatMap(() => [{ wch: 13 }, { wch: 7 }]), { wch: 14 }, { wch: 8 }]
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },
        ...dados.semanas.map((_, k) => ({ s: { r: 0, c: 3 + k * 2 }, e: { r: 0, c: 4 + k * 2 } })),
        { s: { r: 0, c: 3 + dados.semanas.length * 2 }, e: { r: 0, c: 4 + dados.semanas.length * 2 } },
      ]
      for (let r = 2; r < aoa.length; r++) {
        for (let c = 3; c < cab2.length; c += 2) {
          const k = ws[XLSX.utils.encode_cell({ r, c })]; if (k) k.z = '#,##0.00'
        }
      }
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, `Follow Up ${dados.periodo % 100}`)
      XLSX.writeFile(wb, `Follow-Up-vitrine-${dados.periodo % 100}-${Math.floor(dados.periodo / 100)}.xlsx`)
    } catch (e) { setErro((e as Error).message) } finally { setBaixando(false) }
  }

  // `data-rot` = o rótulo que o CELULAR mostra na frente de cada número.
  const Linha = ({ s, grosso, recuo, children }: { s: FuSoma; grosso?: boolean; recuo?: boolean; children: ReactNode }) => (
    <>
      <td className={recuo ? 'pl-9 whitespace-nowrap' : 'whitespace-nowrap'}>{children}</td>
      {s.semanas.map((c, k) => {
        // Semana sem lote: no celular a célula sai do cartão (só diria "–").
        const oculta = dados && !dados.semanas[k].tem_lote ? ' pln-cel-oculta' : ''
        return (
          <Fragment key={k}>
            <td className={n + oculta} data-rot={`${rot[k]} · venda`}>{brl(c.qs)}</td>
            <td className={`${n} ${grosso ? '' : 'text-[var(--pln-tinta-3)]'}${oculta}`} data-rot={`${rot[k]} · novas`}>{qtd(c.f)}</td>
          </Fragment>
        )
      })}
      <td className={n} data-rot="Total · venda">{brl(s.total.qs)}</td>
      <td className={`${n} ${grosso ? '' : 'text-[var(--pln-tinta-3)]'}`} data-rot="Total · novas">{qtd(s.total.f)}</td>
    </>
  )

  return (
    <>
      {erro && <div className="rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-950/40 dark:border-rose-900 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">{erro}</div>}
      {!dados && carregando && <div className="pln-vazio inline-flex items-center gap-2 p-6"><FiLoader className="animate-spin" /> Lendo o Draco…</div>}
      {dados && (
        <>
          <EnviarFollowUp
            api={api}
            envios={dados.envios || []}
            aoEnviar={() => { carregar(); aoMudarRealizado() }}
          />

          <PlnKpis>
            <PlnKpi rotulo="Venda da vitrine" valor={brl(dados.total.total.qs)} pe={`${semanasComNumero.length} de ${dados.semanas.length} semanas com número`} />
            <PlnKpi rotulo="Consultoras novas" valor={qtd(dados.total.total.f)} pe="na vitrine, somando as semanas com número" />
            <PlnKpi rotulo="Grupos com venda" valor={vendendo == null ? '–' : `${vendendo} de ${dados.grupos}`} pe={ultima ? `na semana ${sem(ultima.semana)}` : 'nenhuma semana fechada ainda'} />
            <PlnKpi rotulo="Distritos" valor={String(dados.distritos.length)} pe="a distribuição Campo Grande inteira" />
          </PlnKpis>

          <PlnBloco
            titulo="Follow Up por distrito e grupo"
            nota={`vitrine ${dados.periodo % 100}/${Math.floor(dados.periodo / 100)} · toque no distrito para abrir os grupos dele`}
            extra={
              <div className="flex items-center gap-2">
                <button onClick={carregar} disabled={carregando} className={BTN}>{carregando ? <FiLoader className="animate-spin" /> : <FiRefreshCw />} Atualizar</button>
                <button onClick={baixar} disabled={baixando} className={BTN}>{baixando ? <FiLoader className="animate-spin" /> : <FiDownload />} Excel</button>
              </div>
            }
          >
            <div className="overflow-x-auto pln-gruda">
              <table className="pln-tabela">
                <thead>
                  <tr>
                    <th rowSpan={2} className="text-left align-bottom">Distrito / grupo</th>
                    {dados.semanas.map(s => (
                      <th key={s.semana} colSpan={2} className="text-center">
                        {sem(s.semana)}
                        <span className="block font-normal text-[12px] text-[var(--pln-tinta-3)]">
                          {/* ⚠ Na falta da `fonte`, quem manda é TER NÚMERO
                              (`tem_lote`): semana com venda na coluna não pode
                              dizer "não começou" embaixo do próprio total. */}
                          {s.fonte === 'arquivo' ? 'do relatório do site'
                            : s.fonte === 'draco' || s.tem_lote ? 'apurada no Draco'
                              : s.estado === 'andamento' ? 'falta o relatório do site' : 'não começou'}
                        </span>
                      </th>
                    ))}
                    <th colSpan={2} className="text-center">Total</th>
                  </tr>
                  <tr>
                    {[...dados.semanas.map(s => s.semana), 0].map(k => (
                      <Fragment key={k}>
                        <th className={`${n} font-normal text-[12.5px]`}>Venda</th>
                        <th className={`${n} font-normal text-[12.5px]`}>Novas</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dados.distritos.map(d => (
                    <Fragment key={d.codigo}>
                      <tr onClick={() => vira(d.codigo)} className="clica pln-linha-forte">
                        <Linha s={d} grosso>
                          <span className="inline-flex items-center gap-1.5">
                            {abertos.has(d.codigo) ? <FiChevronDown className="text-[var(--pln-tinta-3)]" /> : <FiChevronRight className="text-[var(--pln-tinta-3)]" />}
                            {d.nome || d.codigo}
                            <span className="font-normal text-[var(--pln-tinta-3)] text-[12.5px]">{d.grupos.length} grupos</span>
                          </span>
                        </Linha>
                      </tr>
                      {abertos.has(d.codigo) && d.grupos.map(g => (
                        <tr key={g.codigo}>
                          <Linha s={g} recuo>
                            {g.nome || g.codigo} <span className="text-[var(--pln-tinta-3)] text-[12px] ml-1">{g.codigo}</span>
                          </Linha>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  <tr className="pln-linha-forte">
                    <Linha s={dados.total} grosso>Campo Grande</Linha>
                  </tr>
                </tbody>
              </table>
            </div>
          </PlnBloco>

          <p className="text-[12.5px] text-[var(--pln-tinta-3)] px-1 leading-relaxed">
            É o relatório <strong>Follow Up</strong>, o mesmo que alimentava a planilha: <strong>Venda</strong> é o “Q$”
            (venda normal + especial da semana) e <strong>Novas</strong> é o “F” (consultoras novas da semana).
            A <strong>Venda</strong> da aba Desafio sai daqui, então as duas abas nunca contam diferente.
            Semana já <strong>apurada</strong> vem do Draco e o arquivo não a altera; a semana que ainda não fechou vem do
            <strong> relatório que você envia aqui em cima</strong>. O lote da semana entra no Draco na noite de segunda ou
            terça — depois disso o apurado assume sozinho.
          </p>
        </>
      )}
    </>
  )
}

function EscolherGrupos({ api, distrito, atuais, fechar, adicionar }: { api: (u: string) => Promise<{ grupos: GrupoDraco[] }>; distrito?: string; atuais: string[]; fechar: () => void; adicionar: (cod: string) => void }) {
  const [grupos, setGrupos] = useState<GrupoDraco[] | null>(null)
  const [busca, setBusca] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  // ⚠ Só os grupos DESTE distrito: com 10 distritos na mesma busca é fácil pôr
  // grupo de outro distrito no desafio sem perceber.
  useEffect(() => {
    api(`/api/desafio-planilha/grupos${distrito ? `?distrito=${distrito}` : ''}`)
      .then(d => setGrupos(d.grupos)).catch(e => setErro((e as Error).message))
  }, [api, distrito])
  const q = busca.trim().toUpperCase()
  const vis = (grupos || []).filter(g => !atuais.includes(g.codigo) && (!q || g.nome.toUpperCase().includes(q) || (g.distrito || '').toUpperCase().includes(q) || g.codigo.includes(q)))
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={fechar}>
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-3 border-b flex items-center gap-2">
          <FiSearch className="text-muted-foreground" />
          <input autoFocus value={busca} onChange={e => setBusca(e.target.value)} placeholder="Grupo, distrito ou código" className="flex-1 bg-transparent outline-none text-sm" />
          <button onClick={fechar}><FiX /></button>
        </div>
        <div className="overflow-auto p-1 text-sm">
          {erro && <div className="p-3 text-rose-600">{erro}</div>}
          {!grupos && !erro && <div className="p-3 text-muted-foreground inline-flex items-center gap-2"><FiLoader className="animate-spin" /> Lendo os grupos…</div>}
          {vis.map(g => (
            <button key={g.codigo} onClick={() => adicionar(g.codigo)} className="w-full text-left px-3 py-1.5 rounded hover:bg-muted flex justify-between gap-3">
              <span>{g.nome}</span><span className="text-muted-foreground text-xs">{g.distrito} · {g.codigo}</span>
            </button>
          ))}
          {grupos && !vis.length && <div className="p-3 text-muted-foreground">Nenhum grupo para adicionar.</div>}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// EVOLUÇÃO — a linha do tempo do desafio (31/10/2026: "faltou um painelzinho
// ou relatório de gráfico sintético com timeline de crescimento").
// Uma coluna por vitrine (as encerradas, a atual e a próxima), cada uma com AS
// REGRAS DELA — o crm monta com a mesma conta da tela (/historico), então o
// ponto do gráfico é o número do cartão daquela vitrine.
//   barra  = realizado (verde-água)   traço = desafio (azul)
// Um eixo só (R$). Atingimento e crescimento são TEXTO sobre/sob a coluna,
// não um segundo eixo. Cores validadas (dataviz validate_palette, claro e
// escuro); o verde-água fica abaixo de 3:1 no claro, por isso os números vão
// escritos e há a visão em tabela.
// ═══════════════════════════════════════════════════════════════════════════
interface HistVitrine {
  periodo: number; vitrine: number; ano: number; tipo: 'passada' | 'atual' | 'proxima'
  regras_de: number | null; desafio: number; realizado: number | null; atingimento: number | null
  acumulado: number | null; semanas_com_realizado: number; diretoria: number
  grupos: { codigo: string; nome: string | null; desafio: number; origem: Origem; realizado: number | null }[]
}
const milhar = (v: number) => (v >= 1e6 ? `${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi` : `${Math.round(v / 1000).toLocaleString('pt-BR')} mil`)
const variacao = (a: number | null, b: number | null) => (a == null || b == null || !b ? null : a / b - 1)

function GraficoEvolucao({ api, distrito, periodoAberto, versao, geral }: { api: ApiFn; distrito: string | null; periodoAberto: number | null; versao: number; geral?: boolean }) {
  const [hist, setHist] = useState<HistVitrine[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [foco, setFoco] = useState<number | null>(null)
  const [vista, setVista] = useState<'vitrines' | 'grupos' | 'tabela'>('vitrines')
  // ⚠ (31/10) O SVG é desenhado na LARGURA REAL do bloco, em pixels. Com viewBox
  // fixo + w-full ele escalava junto com a tela: num monitor largo as letras
  // de 11px viravam 22px e o gráfico passava de 500px de altura.
  const [largura, setLargura] = useState(0)
  const caixa = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    const medir = () => setLargura(el.clientWidth)
    medir()
    const ro = new ResizeObserver(medir)
    ro.observe(el)
  }, [])

  useEffect(() => {
    let vivo = true
    setErro(null)
    api(`/api/desafio-planilha/historico${distrito ? `?distrito=${distrito}` : ''}`)
      .then(d => { if (vivo) setHist((d as unknown as { vitrines: HistVitrine[] }).vitrines) })
      .catch(e => { if (vivo) setErro((e as Error).message) })
    return () => { vivo = false }
  }, [api, distrito, versao])

  const nomeV = (v: HistVitrine) => `${v.vitrine}/${v.ano}`
  const atual = hist?.find(v => v.tipo === 'atual')
  const proxima = hist?.find(v => v.tipo === 'proxima')
  const fechadas = (hist || []).filter(v => v.tipo === 'passada' && v.realizado != null)
  const primeira = fechadas[0]
  const ultima = fechadas[fechadas.length - 1]
  const cresc = variacao(ultima?.realizado ?? null, primeira?.realizado ?? null)

  const abas = (
    <div className="pln-abas" role="tablist">
      <button role="tab" aria-selected={vista === 'vitrines'} onClick={() => setVista('vitrines')} className="pln-aba">Vitrines</button>
      <button role="tab" aria-selected={vista === 'grupos'} onClick={() => setVista('grupos')} className="pln-aba">{geral ? 'Por distrito' : 'Por grupo'}</button>
      <button role="tab" aria-selected={vista === 'tabela'} onClick={() => setVista('tabela')} className="pln-aba"><FiList /> Tabela</button>
    </div>
  )
  const nota = hist && primeira && ultima
    ? <>realizado {nomeV(primeira)} → {nomeV(ultima)}: <strong className={cresc != null && cresc < 0 ? 'pln-ruim' : 'text-[var(--pln-verde)]'}>{cresc != null && cresc >= 0 ? '+' : ''}{pct(cresc)}</strong>
      {proxima && atual ? <> · desafio da {nomeV(proxima)}: <strong className="text-[var(--pln-tinta)]">{brl(proxima.desafio)}</strong> ({(variacao(proxima.desafio, atual.desafio) ?? -1) >= 0 ? '+' : ''}{pct(variacao(proxima.desafio, atual.desafio))} sobre a {nomeV(atual)})</> : null}</>
    : 'realizado × desafio por vitrine'

  if (erro) return <PlnBloco titulo="Evolução"><div className="px-[22px] pb-5 text-[13px] pln-ruim">{erro}</div></PlnBloco>

  // Geometria em pixels de verdade.
  const W = Math.max(320, largura), H = 230, M = { t: 26, r: 8, b: 40, l: 52 }
  const pw = W - M.l - M.r, ph = H - M.t - M.b
  const lista = hist || []
  const topo = Math.max(1, ...lista.map(v => Math.max(v.desafio, v.realizado || 0))) * 1.1
  const passo = (() => { const bruto = topo / 4; const p10 = 10 ** Math.floor(Math.log10(bruto)); return [1, 2, 2.5, 5, 10].map(k => k * p10).find(k => k >= bruto) || bruto })()
  const ticks = Array.from({ length: Math.floor(topo / passo) + 1 }, (_, i) => i * passo)
  const y = (v: number) => M.t + ph - (v / topo) * ph
  const col = lista.length ? pw / lista.length : pw
  const larg = Math.min(34, col * 0.42)
  const cx = (i: number) => M.l + col * i + col / 2
  const anteriorReal = (i: number) => (i > 0 ? lista[i - 1].realizado : null)
  const f = foco != null ? lista[foco] : null

  const daTela = lista.find(v => v.periodo === periodoAberto) || atual
  const porGrupo = (daTela?.grupos || []).filter(g => g.realizado != null && g.desafio > 0)
    .map(g => ({ ...g, at: (g.realizado as number) / g.desafio })).sort((a, b) => b.at - a.at)
  const maxAt = Math.max(1.1, ...porGrupo.map(g => g.at))

  return (
    <PlnBloco titulo="Evolução" nota={nota} extra={abas}>
      <div className="px-[22px] pb-5" ref={caixa}>
        {!hist ? (
          <div className="pln-vazio inline-flex items-center gap-2 py-6"><FiLoader className="animate-spin" /> Montando a linha do tempo…</div>
        ) : vista === 'tabela' ? (
          <div className="overflow-x-auto">
            <table className="pln-tabela">
              <thead><tr><th className="text-left">Vitrine</th><th className={n}>Desafio</th><th className={n}>Realizado</th><th className={n}>Atingimento</th><th className={n}>Cresc. do realizado</th><th className="text-left">Regras</th></tr></thead>
              <tbody>
                {lista.map((v, i) => (
                  <tr key={v.periodo}>
                    <td>{nomeV(v)}{v.tipo !== 'passada' && <span className="text-[var(--pln-tinta-3)] text-[12.5px] ml-1">{v.tipo === 'atual' ? 'atual' : 'próxima'}</span>}</td>
                    <td className={n} data-rot="Desafio">{brl(v.desafio)}</td>
                    <td className={n} data-rot="Realizado">{brl(v.realizado)}</td>
                    <td className={n} data-rot="Atingimento"><Selo v={v.tipo === 'atual' ? v.acumulado : v.atingimento} /></td>
                    <td className={n} data-rot="Cresc. do realizado">{v.tipo === 'passada' ? pct(variacao(v.realizado, anteriorReal(i))) : '–'}</td>
                    <td data-rot="Regras" className="text-[12.5px] text-[var(--pln-tinta-2)]">{v.regras_de === v.periodo ? 'próprias' : v.regras_de ? `da ${v.regras_de % 100}/${Math.floor(v.regras_de / 100)}` : 'gerais'}{v.diretoria ? ` · ${v.diretoria} da diretoria` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : vista === 'grupos' ? (
          porGrupo.length ? (
            <>
              <div className="text-[12.5px] text-[var(--pln-tinta-3)] mb-2">Atingimento do mês por {geral ? 'distrito' : 'grupo'} · vitrine {daTela ? nomeV(daTela) : ''}{daTela?.tipo === 'atual' ? ' (até agora)' : ''} · traço azul = 100% do desafio</div>
              <div className="grid gap-x-8 gap-y-1.5 md:grid-cols-2">
                {porGrupo.map(g => (
                  <div key={g.codigo} className="grid grid-cols-[minmax(0,150px)_1fr_44px] items-center gap-2.5 text-[12.5px]" title={`Desafio R$ ${inteiro(g.desafio)} · realizado R$ ${inteiro(g.realizado || 0)}`}>
                    <span className="truncate text-[var(--pln-tinta-2)]">{g.nome || g.codigo}</span>
                    <span className="relative h-2 rounded-full bg-[var(--pln-fio)]">
                      <span className="absolute inset-y-0 left-0 rounded-full bg-[#1baf7a] dark:bg-[#199e70]" style={{ width: `${Math.min(100, (g.at / maxAt) * 100)}%` }} />
                      <span className="absolute -top-1 -bottom-1 w-[2px] rounded bg-[#2a78d6] dark:bg-[#3987e5]" style={{ left: `${(1 / maxAt) * 100}%` }} />
                    </span>
                    <span className="text-right tabular-nums font-semibold text-[var(--pln-tinta)]">{pct(g.at, 0)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="pln-vazio py-4 text-[13px]">Esta vitrine ainda não tem realizado.</div>
        ) : (
          <div className="relative" onMouseLeave={() => setFoco(null)}>
            {/* Legenda: 2 séries, forma + cor (barra × traço), nunca só cor. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[var(--pln-tinta-2)]">
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-[3px] bg-[#1baf7a] dark:bg-[#199e70]" /> Realizado</span>
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3.5 h-[3px] rounded bg-[#2a78d6] dark:bg-[#3987e5]" /> Desafio</span>
              <span className="text-[var(--pln-tinta-3)]">% sobre a coluna = atingimento · sob a vitrine = crescimento do realizado</span>
            </div>
            <svg width={W} height={H} className="block" role="img" aria-label="Realizado e desafio por vitrine">
              {ticks.map(t => (
                <g key={t}>
                  <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} stroke="var(--pln-fio)" strokeWidth={1} />
                  <text x={M.l - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--pln-tinta-3)" style={{ fontVariantNumeric: 'tabular-nums' }}>{t ? milhar(t) : '0'}</text>
                </g>
              ))}
              {lista.map((v, i) => {
                const x = cx(i)
                const real = v.realizado
                const at = v.tipo === 'atual' ? v.acumulado : v.atingimento
                const c = variacao(real, anteriorReal(i))
                const topoCol = Math.min(y(v.desafio), real != null ? y(real) : Infinity)
                const yr = real != null ? y(real) : 0
                return (
                  <g key={v.periodo} opacity={foco != null && foco !== i ? 0.4 : 1}>
                    {v.tipo === 'atual' && <rect x={M.l + col * i + 3} y={M.t - 6} width={col - 6} height={ph + 6} rx={6} fill="var(--pln-ciano-claro)" opacity={0.6} />}
                    {real != null && real > 0 && (
                      <path d={`M${x - larg / 2},${y(0)} V${yr + 4} Q${x - larg / 2},${yr} ${x - larg / 2 + 4},${yr} H${x + larg / 2 - 4} Q${x + larg / 2},${yr} ${x + larg / 2},${yr + 4} V${y(0)} Z`}
                        className={v.tipo === 'atual' ? 'fill-[#1baf7a]/60 dark:fill-[#199e70]/60' : 'fill-[#1baf7a] dark:fill-[#199e70]'} />
                    )}
                    <line x1={x - larg / 2 - 5} x2={x + larg / 2 + 5} y1={y(v.desafio)} y2={y(v.desafio)} stroke="var(--pln-papel)" strokeWidth={6} strokeLinecap="round" />
                    <line x1={x - larg / 2 - 5} x2={x + larg / 2 + 5} y1={y(v.desafio)} y2={y(v.desafio)} className="stroke-[#2a78d6] dark:stroke-[#3987e5]" strokeWidth={2.5} strokeLinecap="round" strokeDasharray={v.tipo === 'proxima' ? '4 3' : undefined} />
                    <text x={x} y={topoCol - 7} textAnchor="middle" fontSize={11.5} fontWeight={600} fill={v.tipo === 'proxima' ? 'var(--pln-tinta-3)' : 'var(--pln-tinta)'}>{v.tipo === 'proxima' ? 'objetivo' : at != null ? pct(at, 0) : ''}</text>
                    <text x={x} y={H - M.b + 15} textAnchor="middle" fontSize={11.5} fontWeight={v.tipo === 'passada' ? 400 : 600} fill={v.tipo === 'passada' ? 'var(--pln-tinta-2)' : 'var(--pln-tinta)'}>{nomeV(v)}</text>
                    <text x={x} y={H - M.b + 29} textAnchor="middle" fontSize={10.5} fill="var(--pln-tinta-3)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {v.tipo === 'passada' && c != null ? `${c >= 0 ? '+' : '−'}${Math.abs(Math.round(c * 100))}%` : v.tipo === 'atual' ? 'atual' : v.tipo === 'proxima' ? 'próxima' : ''}
                    </text>
                    {/* Alvo do hover = a coluna inteira, maior que a marca. */}
                    <rect x={M.l + col * i} y={M.t - 20} width={col} height={ph + 40} fill="transparent" onMouseEnter={() => setFoco(i)} onClick={() => setFoco(i)} />
                  </g>
                )
              })}
            </svg>
            {f && foco != null && (
              <div className="pointer-events-none absolute top-8 z-10 min-w-[190px] rounded-lg border border-[var(--pln-borda)] bg-[var(--pln-papel)] px-3 py-2 text-[12px] shadow-lg"
                style={{ left: cx(foco), transform: foco > lista.length / 2 ? 'translateX(calc(-100% - 22px))' : 'translateX(22px)' }}>
                <div className="font-semibold text-[var(--pln-tinta)] mb-1">Vitrine {nomeV(f)}{f.tipo !== 'passada' ? ` · ${f.tipo === 'atual' ? 'atual' : 'próxima'}` : ''}</div>
                <div className="flex justify-between gap-4"><span className="text-[var(--pln-tinta-2)]">Desafio</span><span className="tabular-nums">{brl(f.desafio)}</span></div>
                <div className="flex justify-between gap-4"><span className="text-[var(--pln-tinta-2)]">Realizado</span><span className="tabular-nums">{brl(f.realizado)}</span></div>
                <div className="flex justify-between gap-4"><span className="text-[var(--pln-tinta-2)]">Atingimento</span><span className="tabular-nums">{pct(f.tipo === 'atual' ? f.acumulado : f.atingimento)}</span></div>
                {f.tipo === 'passada' && <div className="flex justify-between gap-4"><span className="text-[var(--pln-tinta-2)]">Cresc. do realizado</span><span className="tabular-nums">{pct(variacao(f.realizado, anteriorReal(foco)))}</span></div>}
                {f.tipo === 'atual' && <div className="text-[var(--pln-tinta-3)] mt-1">{f.semanas_com_realizado} de 4 semanas com realizado</div>}
                {f.diretoria > 0 && <div className="text-[var(--pln-tinta-3)] mt-1">{f.diretoria} grupo(s) com desafio da diretoria</div>}
              </div>
            )}
          </div>
        )}
      </div>
    </PlnBloco>
  )
}
